import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // Alles in einer Zeile
import GlobalLayout from './GlobalLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getSecureMediaUrl } from '../utils/mediaUtils';
import { appConfig } from '../config'; 
import './ElementCreator.css';
import { generateScriptSuggestions, addElementToCollection } from '../services/apiService';
// @mmote/niimbluelib wird dynamisch geladen (kein SSR-Crash)


// --- SECURITY HELPER ---
const sanitizeInput = (str) => {
    if (!str) return '';
    // Entfernt HTML Tags (<...>)
    let clean = str.replace(/<[^>]*>?/gm, '');
    return clean.trim();
};

// --- SCRIPT PREFIX STRIPPER ---
// Das LLM liefert manchmal Prefixe wie "Option 3 (Concise): " oder "Option 2 - Detailed: "
// Diese werden vor Anzeige und Übernahme entfernt.
const stripScriptPrefix = (text) => {
  if (!text) return text;
  // Matcht: "Option 3:", "Option 3 (Concise):", "Option 3 - Concise:", "Option 3 – Concise:"
  return text.replace(/^Option\s+\d+\s*(\([^)]*\)|[\-–][^:]*)?:\s*/i, '').trim();
};

// --- DOCUMENT HELPER ---
const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // Microsoft Excel
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const getDocumentIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType === 'application/vnd.ms-powerpoint' || mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return '📊';
    if (mimeType === 'application/vnd.ms-excel' || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return '📗';
    if (mimeType === 'text/plain') return '📝';
    if (mimeType?.includes('word') || mimeType?.includes('msword')) return '📃';
    return '📁';
};

const getDocumentTypeName = (mimeType) => {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType === 'application/vnd.ms-powerpoint') return 'PPT';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'PPTX';
    if (mimeType === 'application/vnd.ms-excel') return 'XLS';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'XLSX';
    if (mimeType === 'text/plain') return 'TXT';
    if (mimeType === 'application/msword') return 'DOC';
    if (mimeType?.includes('wordprocessingml')) return 'DOCX';
    return 'Dokument';
};

// ── Fast-lane: QR-Code clientseitig rendern ──────────────────────────────────
// Wird genutzt wenn noch kein Server-seitiges QR-Bild existiert (status=queued).
// Nutzt dieselbe qrcode-Library wie QRCodeDisplay in AdminPanel.
const FastlaneQRCode = ({ url, size = 160 }) => {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    if (!url || !canvasRef.current) return;
    import('qrcode').then(mod => {
      const QRCode = mod.default || mod;
      QRCode.toCanvas(canvasRef.current, url, {
        width: size, margin: 1,
        color: { dark: '#111827', light: '#ffffff' },
      }).catch(console.error);
    }).catch(console.error);
  }, [url, size]);
  return (
    <div style={{
      borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden',
      background: '#fff', display: 'inline-flex',
    }}>
      <canvas ref={canvasRef} width={size} height={size} />
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const ElementCreator = () => {
  const navigate = useNavigate();
  const location = useLocation(); 

  // 1. NEUE STATES HINZUFÜGEN (Am Anfang der Komponente)
  const [showScriptSelection, setShowScriptSelection] = useState(false); // Steuert, ob wir im Zwischenschritt sind
  const [scriptOptions, setScriptOptions] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptRetryCount, setScriptRetryCount] = useState(0);
  const [isLoadingScripts, setIsLoadingScripts] = useState(false);

  // NEU: Upload-Modus State (Tabs in Step 2)
  const [uploadMode, setUploadMode] = useState('media'); // 'media' | 'document'

  // ── Dokument-Vorschau-Zwischenschritt ────────────────────────────────────────
  const [showDocPreview, setShowDocPreview]         = useState(false);
  const [docPreviewLoading, setDocPreviewLoading]   = useState(false);
  const [docPreviewData, setDocPreviewData]         = useState(null); // {doc_name, doc_description, ...}
  const [docPreviewName, setDocPreviewName]         = useState('');
  const [docPreviewDesc, setDocPreviewDesc]         = useState('');

  const { t, currentLanguage, isLoadingLang } = useLanguage();
  const { user, token, hasFeature, getElement, getValidToken, makeAuthenticatedRequest } = useAuth();

  // Access-Guard: nur wenn features.element_creator aktiv
  useEffect(() => {
    if (!hasFeature('element_creator')) {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admins mit admin_panel-Feature: Toggle ob Inhalt der "Allgemein"-Gruppe zugeordnet wird
  const isAdminUser = hasFeature('admin_panel'); // group 0, 2, 3
  // Reaktionsvideo nur für SuperAdmin (0) — nicht für TenantAdmin (3) oder Supervisor (2)
  const isReactionVideoAdmin = user && parseInt(user.group) === 0;
  const [assignToGeneral, setAssignToGeneral] = useState(true);

  // Wizard Steps: 
  // 1: Name
  // 2: Media (Upload/Camera) or Document
  // 3: Description
  // 4: Review & Submit
  // 5: Processing (Loading)
  const [step, setStep] = useState(1);
  
  // Form Data State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mediaFile: null,
    mediaType: null, // 'video' or 'image'
    useExactText: false,
    isReactionVideo: false,
    useVideoTranscription: false,  // NEU: Ton des Videos transkribieren und als Voiceover nutzen
    useOriginalAudio: false,       // NEU: Originalton des Videos 1:1 übernehmen (nur Quellsprache)
    mediaPreviewUrl: null,
    // NEU: Dokument-Felder
    documentFile: null,
    documentFileName: null,
    documentMimeType: null,
    isDocumentMode: false,  // true wenn Dokument statt Foto/Video
  });

  // --- NEU: INTELLIGENTE SCHLIESSEN-FUNKTION ---
  const handleClose = () => {
      // Prüfen, ob eine Rücksprung-Adresse im State mitgegeben wurde
      if (location.state && location.state.returnUrl) {
          navigate(location.state.returnUrl);
      } else {
          // Fallback: Wenn User direkt kam oder kein State da ist -> Dashboard
          navigate('/profile');
      }
  };

  // --- API CALL ÜBER SERVICE ---
  const fetchScriptSuggestions = async () => {
    setIsLoadingScripts(true);
    setError('');
    
    // Im Videoton-Modus: Transkript als Basis für Vorschläge (statt formData.description)
    const descToUse = videoToneTranscript || formData.description;
    const langToUse = videoToneLanguage   || currentLanguage;

    try {
        const data = await generateScriptSuggestions(
            formData.name, 
            descToUse, 
            langToUse
        );

        if (data && data.options && Array.isArray(data.options)) {
            let finalOptions = data.options;

            // --- NEU: Für Gruppe 0 Original-Text am ENDE anfügen ---
            if (hasFeature('super_admin')) {
                // Wir nehmen die KI-Vorschläge und hängen das Original hinten an
                finalOptions = [...finalOptions, descToUse];
            }
            // -------------------------------------------------------

            setScriptOptions(finalOptions);
        } else {
            setScriptOptions([]);
            console.warn("Keine Optionen erhalten", data);
        }
    } catch (e) {
        console.error("Fehler beim Laden der Skripte:", e);
        setError(t('errorLoadingScripts') || "Fehler beim Laden der Vorschläge.");
    } finally {
        setIsLoadingScripts(false);
    }
  };

  const [error, setError] = useState('');
  const [stepError, setStepError] = useState('');
  const [uploadErrorType, setUploadErrorType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // --- Superadmin Transkript-Review-States (Step 3.5) ---
  // Aktiv wenn: group=0 + useVideoTranscription + useExactText
  const [transcriptPreview, setTranscriptPreview] = useState('');
  const [transcriptPreviewEdited, setTranscriptPreviewEdited] = useState('');
  const [transcriptPreviewLanguage, setTranscriptPreviewLanguage] = useState('');
  const [transcriptPreviewSegments, setTranscriptPreviewSegments] = useState([]);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [showTranscriptReview, setShowTranscriptReview] = useState(false);
  // Gecachtes Transkript aus "Videoton + Script-Vorschläge"-Flow
  // Wird in handleTranscriptForSuggestions gesetzt, in handleSubmit wiederverwendet.
  const [videoToneTranscript, setVideoToneTranscript] = useState('');
  const [videoToneLanguage, setVideoToneLanguage] = useState('');
  // Neuer State für das Ergebnis nach dem Erstellen
  const [createdElementData, setCreatedElementData] = useState(null);
  // Video-Bereitschafts-State für Success-Screen:
  // null = noch nicht geprüft, false = FFmpeg läuft noch, true = bereit
  const [videoReady, setVideoReady] = useState(null);
  const videoReadyPollRef = useRef(null);

  // --- VIDEO READINESS POLL ---
  // Prüft alle 5s ob stream_0.m3u8 (erster HLS-Segment-Index, nur von FFmpeg erzeugt)
  // tatsächlich erreichbar ist. master.m3u8 existiert bereits synchron — es ist
  // KEIN zuverlässiges Signal für Abspielbereitschaft.
  //
  // Prüfmethode: HEAD auf stream_0.m3u8 via getSecureMediaUrl() — diese Funktion
  // fügt die korrekte Media-Proxy-Auth hinzu (gleiche Methode wie ElementFeed/ElementPage).
  //   → 200/206: FFmpeg fertig, Datei existiert → Button freischalten
  //   → 404:     FFmpeg läuft noch, Datei fehlt  → weiter warten
  //   → 401:     Auth-Problem (sollte nicht auftreten) → weiter warten
  // Timeout nach 25 Versuchen (~2 min).
  useEffect(() => {
    if (!createdElementData?.videoUrl) return;
    if (createdElementData?.pending_approval) return;

    // stream_0.m3u8 = erster Sprach-Track, wird ausschließlich von FFmpeg geschrieben
    const masterUrl   = createdElementData.videoUrl;
    const rawStreamUrl = masterUrl.replace('master.m3u8', 'stream_0.m3u8');
    const streamUrl    = getSecureMediaUrl(rawStreamUrl); // Media-Proxy-Auth hinzufügen

    setVideoReady(null); // Spinner anzeigen
    let attempts = 0;
    const MAX = 25;

    videoReadyPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(streamUrl, { method: 'HEAD' });
        if (res.ok) {
          // 200/206: Datei existiert → FFmpeg fertig
          setVideoReady(true);
          clearInterval(videoReadyPollRef.current);
          videoReadyPollRef.current = null;
          return;
        }
        // 404: Datei fehlt noch → weiter warten (kein setVideoReady(false) nötig)
      } catch (_) { /* Netzwerkfehler — nächster Versuch */ }

      if (attempts >= MAX) {
        // Timeout: nach ~2 min Button trotzdem freigeben
        setVideoReady(true);
        clearInterval(videoReadyPollRef.current);
        videoReadyPollRef.current = null;
      } else {
        setVideoReady(false);
      }
    }, 5000);

    return () => {
      if (videoReadyPollRef.current) {
        clearInterval(videoReadyPollRef.current);
        videoReadyPollRef.current = null;
      }
    };
  }, [createdElementData?.videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- PROGRESS BAR STATES ---
  const [progressValue, setProgressValue] = useState(0);   // 0–100
  const [progressStageIdx, setProgressStageIdx] = useState(0);

  // --- NIIMBOT DRUCKER STATES ---
  const [printerClient, setPrinterClient] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printStatus, setPrintStatus] = useState(''); // 'idle' | 'connecting' | 'printing' | 'done' | 'error'
  const printCanvasRef = useRef(null);

  // ── Batch-Upload State
  const [batchMode, setBatchMode]             = useState(false);
  const [batchType, setBatchType]             = useState('document');
  const [batchFiles, setBatchFiles]           = useState([]);
  const [batchResult, setBatchResult]         = useState(null);
  const [batchPrinting, setBatchPrinting]     = useState(false);
  const [batchPrintIdx, setBatchPrintIdx]     = useState(-1);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const batchFileInputRef = useRef(null);
  // Batch-Einstellungen (analog single upload, typ-abhängig)
  const [batchAssignToGeneral, setBatchAssignToGeneral]   = useState(true);
  const [batchUseExactText, setBatchUseExactText]         = useState(false);
  const [batchUseOriginalAudio, setBatchUseOriginalAudio] = useState(false);
  const [batchDescription, setBatchDescription]           = useState('');
  const [batchStep, setBatchStep]                         = useState(1); // 1=Upload, 2=Einstellungen
  const [dragIndex, setDragIndex]                         = useState(null);
  const [dragOverIndex, setDragOverIndex]                 = useState(null);
  const touchDragRef = useRef({ active: false, fromIndex: null, toIndex: null });
  // Per-Slide-Narration (Foto-Batch-Modus)
  const [slideTexts, setSlideTexts]         = useState([]); // [text_slide0, text_slide1, ...]
  const [photoThumbs, setPhotoThumbs]       = useState([]); // Object-URLs für Thumbnails
  const [photoLightboxIdx, setPhotoLightboxIdx] = useState(null); // null = zu, n = Foto n im Modal
  
  // Refs
  const fileInputRef = useRef(null);
  const documentFileInputRef = useRef(null); // NEU: Ref für Dokument-Upload
  const videoPreviewRef = useRef(null);

  // Tenant-Script-Konfiguration (pro Mandant, ersetzt globales ENABLE_SCRIPT_REVIEW)
  const [tenantScriptConfig, setTenantScriptConfig] = useState(null);

  useEffect(() => {
    makeAuthenticatedRequest('/admin/config')
      .then(res => {
        if (res && res.status === 'ok') setTenantScriptConfig(res);
      })
      .catch(() => {}); // Fehler still ignorieren — Fallback: keine Vorschläge
  }, []); // einmalig beim Mount

  // Abgeleitet aus Tenant-Config + User-Gruppe:
  // - Solange Config noch lädt (null): false (kein Vorschlag-Step)
  // - Admins/Supervisors (0,2,3): script_suggestions_admin (default true)
  // - Standard-User (1): script_suggestions_user (default false)
  const userGroup = user ? parseInt(user.group) : 1;
  const isAdminOrSupervisor = [0, 2, 3].includes(userGroup);
  const enableScriptReview = tenantScriptConfig === null
    ? false
    : isAdminOrSupervisor
      ? (tenantScriptConfig.script_suggestions_admin ?? true)
      : (tenantScriptConfig.script_suggestions_user ?? false);

  // "Text exakt übernehmen": immer für group 0,2,3; für group 1 nur wenn allow_exact_text_user aktiv
  const canUseExactText = isAdminUser || (tenantScriptConfig?.allow_exact_text_user === true);
  // Dokument-Vorschau-Schritt: group 0/2/3 immer, group 1 nur wenn Flag aktiv
  const canShowDocPreview = [0, 2, 3].includes(userGroup) || (tenantScriptConfig?.allow_doc_preview_user === true);
  // "Videoton als Voiceover": immer für group 0,2,3; für group 1 nur wenn allow_video_tone_user aktiv
  const canUseVideoTone = isAdminUser || (tenantScriptConfig?.allow_video_tone_user === true);

  // "Originalton übernehmen": aktuell nur Group 0 (SuperAdmin).
  // Architektur erlaubt spätere Erweiterung via Config-Flag (allow_original_audio_user / group-check).
  // Nie hardcoden — immer über diese Konstante prüfen.
  const canUseOriginalAudio = userGroup === 0 || (tenantScriptConfig?.allow_original_audio_user === true);


  // Cleanup Object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (formData.mediaPreviewUrl) {
        URL.revokeObjectURL(formData.mediaPreviewUrl);
      }
    };
  }, [formData.mediaPreviewUrl]);

  // --- PROGRESS BAR LOGIK ---
  // Das Frontend schickt beim Submit einen progress_token mit.
  // Während der POST /create-element läuft, pollt ein Interval alle 800ms
  // GET /element-progress?token={token} und aktualisiert Bar + Label.
  const [progressToken]  = useState(() => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const progressPollRef    = useRef(null);
  const simStartRef        = useRef(null);  // Timestamp für asymptotische Simulation
  const simIntervalRef     = useRef(null);  // Simulation-Interval-Handle
  const lastRealPctRef     = useRef(0);     // Letzter echter Backend-Wert

  useEffect(() => {
    // ── Hilfsfunktion: alle Intervals stoppen ─────────────────────────────
    const stopAll = () => {
      if (progressPollRef.current) { clearInterval(progressPollRef.current); progressPollRef.current = null; }
      if (simIntervalRef.current)  { clearInterval(simIntervalRef.current);  simIntervalRef.current  = null; }
    };

    if (!isProcessing) {
      stopAll();
      if (progressValue > 0) {
        setProgressValue(95);
        // Stage-Idx auf vorletzten (Speichern) setzen
        const lastStageIdx = formData.isDocumentMode ? 5 : 4;
        setProgressStageIdx(lastStageIdx);
        const tid = setTimeout(() => {
          setProgressValue(0);
          setProgressStageIdx(0);
          lastRealPctRef.current = 0;
        }, 700);
        return () => clearTimeout(tid);
      }
      return;
    }

    // ── Stage-Ankerpunkte je Modus ────────────────────────────────────────
    //
    // Jede Stage: { pct: Ziel-%, duration: ms bis zum nächsten Anker, stageIdx }
    // Die Simulation springt nicht — sie interpoliert linear zwischen Ankern.
    //
    // Dokument-Modus (document_mode=1):
    //   VideoBuilder: 2→8(mod)→32(text)→48(img)→62(summary)→68(transl)→88(audio×10)→92%
    //   Typische Gesamtdauer: 90–180s
    //
    // Video-Modus (document_mode=0):
    //   VideoBuilder: 2→8(mod)→25(script,opt)→40(transl)→50-80(audio×10)→88(db)→92%
    //   Typische Gesamtdauer: 60–120s

    const isDoc = formData.isDocumentMode;

    const docStages = [
      { pct:  2, duration:  4000, stageIdx: 0 }, // Hochladen
      { pct:  8, duration:  5000, stageIdx: 0 }, // Moderation
      { pct: 32, duration: 18000, stageIdx: 1 }, // Text extrahieren
      { pct: 48, duration: 12000, stageIdx: 2 }, // Seiten analysieren
      { pct: 62, duration: 22000, stageIdx: 3 }, // Zusammenfassung (LLM)
      { pct: 68, duration:  8000, stageIdx: 3 }, // Übersetzungen
      { pct: 88, duration: 75000, stageIdx: 4 }, // Voiceover × 10 Sprachen (längste Phase)
      { pct: 92, duration: 10000, stageIdx: 5 }, // DB + Vektorisierung
    ];

    const videoStages = [
      { pct:  2, duration:  3000, stageIdx: 0 }, // Hochladen
      { pct:  8, duration:  4000, stageIdx: 0 }, // Moderation
      { pct: 25, duration: 20000, stageIdx: 1 }, // Skript-Generierung / Verarbeiten
      { pct: 40, duration:  8000, stageIdx: 2 }, // Übersetzungen
      { pct: 80, duration: 60000, stageIdx: 3 }, // Voiceover × 10 Sprachen
      { pct: 92, duration:  8000, stageIdx: 4 }, // DB + Speichern
    ];

    const stages    = isDoc ? docStages : videoStages;
    let stageIndex  = 0;
    let stageStart  = Date.now();

    setProgressValue(2);
    setProgressStageIdx(stages[0].stageIdx);
    lastRealPctRef.current = 0;
    simStartRef.current    = Date.now();

    // ── Stufenbasierte Simulation ──────────────────────────────────────────
    // Interpoliert linear zwischen Stage-Ankern. Reale Backend-Werte gewinnen
    // via Math.max — sie bleiben immer Untergrenze.
    simIntervalRef.current = setInterval(() => {
      if (stageIndex >= stages.length - 1) return; // letzte Stage erreicht

      const current = stages[stageIndex];
      const next    = stages[stageIndex + 1];
      const elapsed = Date.now() - stageStart;
      const frac    = Math.min(elapsed / current.duration, 1);

      // Lineare Interpolation zwischen aktuellem und nächstem Anker
      const simPct  = Math.round(current.pct + frac * (next.pct - current.pct));
      const display = Math.max(simPct, lastRealPctRef.current, 2);

      setProgressValue(Math.min(display, 92));
      setProgressStageIdx(current.stageIdx);

      // Nächste Stage betreten wenn Zeit abgelaufen
      if (frac >= 1) {
        stageIndex++;
        stageStart = Date.now();
        if (stageIndex < stages.length) {
          setProgressStageIdx(stages[stageIndex].stageIdx);
        }
      }
    }, 300);

    // ── Echter Backend-Poll (best-effort, gewinnt immer) ──────────────────
    const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');

    progressPollRef.current = setInterval(async () => {
      try {
        // getValidToken() gibt immer ein gültiges Token zurück (refresht bei Bedarf)
        const authToken = await getValidToken() || '';
        const res  = await fetch(`${baseUrl}/element-progress?token=${progressToken}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
        });
        if (!res.ok) {
          if (res.status === 404) {
            // 404 = Progress-Token abgelaufen → Polling stoppen, kein Logout
            clearInterval(progressPollRef.current);
            progressPollRef.current = null;
          }
          // 401 hier NICHT stoppen — getValidToken() hat bereits refresht, temporärer Fehler
          return;
        }
        const data = await res.json();

        if (data.pct > 0) {
          const cappedPct = Math.min(data.pct, 92);
          // Realen Wert als neue Untergrenze setzen
          if (cappedPct > lastRealPctRef.current) {
            lastRealPctRef.current = cappedPct;
            setProgressValue(cappedPct);
          }

          // Stage-Index aus Backend-Stage-Name ableiten
          const stageMap = isDoc ? {
            start: 0, moderation: 0,
            text_extract: 1, doc_meta: 1, text_done: 1,
            img_extract: 2, img_extracted: 2, img_filtered: 2,
            summary: 3, summary_done: 3, thumbnail: 3, translations: 3,
            audio_de: 4, audio_en: 4, audio_fr: 4, audio_es: 4,
            audio_ru: 4, audio_pl: 4, audio_ar: 4, audio_ro: 4, audio_tr: 4, audio_hr: 4,
            db_save: 5, vectorize: 5,
            ffmpeg: 6, done: 6,
          } : {
            start: 0, moderation: 0,
            script_gen: 1, transcribing: 1,
            translations: 2,
            audio_de: 3, audio_en: 3, audio_fr: 3, audio_es: 3,
            audio_ru: 3, audio_pl: 3, audio_ar: 3, audio_ro: 3, audio_tr: 3, audio_hr: 3,
            db_save: 4, thumbnail: 4, vectorize: 4,
            ffmpeg: 5, done: 5,
          };
          const backendIdx = stageMap[data.stage];
          if (backendIdx !== undefined) {
            // Backend-Stage vorwärts scrubben (nie zurückspringen)
            if (backendIdx > stageIndex) {
              stageIndex = backendIdx;
              stageStart = Date.now();
            }
            setProgressStageIdx(backendIdx);
          }
        }

        if (data.done) {
          clearInterval(progressPollRef.current);
          progressPollRef.current = null;
        }
      } catch (_) { /* Netzwerkfehler ignorieren – Simulation läuft weiter */ }
    }, 800);

    return stopAll;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  // Wenn Upload-Modus wechselt: nur isDocumentMode umschalten.
  // Dateien der jeweils anderen Seite werden NICHT gelöscht, damit beim
  // Zurückwechseln die bereits hochgeladene Datei noch sichtbar ist.
  // Submit-Logik entscheidet anhand von isDocumentMode welche Datei gesendet wird.
  // batchMode wird deaktiviert, damit die Tab-Anzeige konsistent bleibt.
  const handleUploadModeChange = (newMode) => {
    setUploadMode(newMode);
    setBatchMode(false);
    setError('');
    setFormData(prev => ({
      ...prev,
      isDocumentMode: newMode === 'document',
    }));
  };

  // --- HANDLERS ---

  // ── Dokument-Vorschau: API-Call an /preview-document ─────────────────────────
  const handleDocPreview = async () => {
    if (!formData.documentFile) return;
    setDocPreviewLoading(true);
    setShowDocPreview(true);
    setDocPreviewData(null);
    try {
      const fd = new FormData();
      fd.append('document_file', formData.documentFile);
      fd.append('src_lang', 'de'); // TODO: aus User-Sprache ableiten
      const res = await makeAuthenticatedRequest('/preview-document', {
        method: 'POST',
        body: fd,
        // makeAuthenticatedRequest setzt Content-Type nicht manuell → Browser setzt multipart/form-data
      });
      if (res && res.status === 'ok') {
        setDocPreviewData(res);
        setDocPreviewName(res.doc_name || '');
        setDocPreviewDesc(res.doc_description || '');
      } else {
        // Fehler → trotzdem Vorschau anzeigen, aber leer
        setDocPreviewData({});
        setDocPreviewName('');
        setDocPreviewDesc('');
      }
    } catch (e) {
      setDocPreviewData({});
      setDocPreviewName('');
      setDocPreviewDesc('');
    } finally {
      setDocPreviewLoading(false);
    }
  };

  // Vorschau bestätigen → handleSubmit mit Override
  const handleDocPreviewConfirm = () => {
    setShowDocPreview(false);
    // Name und Beschreibung werden als zusätzliche Felder mitgesendet
    handleSubmit({ doc_name_override: docPreviewName, doc_desc_override: docPreviewDesc });
  };

  // --- Superadmin Transkript-Preview (Step 3.5) ---
  // Wird aufgerufen wenn group=0, useVideoTranscription=true, useExactText=true
  // Transkribiert das Video vorab via /transcribe-video und zeigt das Ergebnis
  // in einem editierbaren Textarea — BEVOR der eigentliche Submit startet.

  /**
   * Client-seitige Pendant zu HejViHelper::normalizeText() im PHP-Backend.
   * Korrigiert Whisper-Fehlerkennungen von "HejVi" (Helvi, Hevi, HeyVi, …)
   * bevor der Text in der Preview-Textarea angezeigt wird.
   * Nur sichere Varianten — keine echten Wörter in irgendeiner Sprache.
   */
  const normalizeHejVi = (text) => {
    const variants = [
      /\bHejvi\b/gu,
      /\bHEJVI\b/gu,
      /\bhejvi\b/gu,
      /\bHeyVi\b/gu,
      /\bHey-Vi\b/gu,
      /\bHevi\b/gu,
      /\bHevy\b/gu,
      /\bHelvi\b/gu,
      /\bhelvi\b/gu,
      /\bHELVI\b/gu,
      /\bHeyvi\b/gu,   // Whisper schreibt manchmal "Heyvi" statt "HejVi"
      /\bheyvi\b/gu,
      /\bHEYVI\b/gu,
    ];
    return variants.reduce((t, re) => t.replace(re, 'HejVi'), text);
  };

  const handleTranscriptPreview = async () => {
    setIsTranscriptLoading(true);
    setError('');
    try {
      const transcribeForm = new FormData();
      transcribeForm.append('video', formData.mediaFile);

      // makeAuthenticatedRequest: auto-refresh + retry bei 401 (kein raw fetch)
      const result = await makeAuthenticatedRequest('/transcribe-video', {
        method: 'POST',
        body: transcribeForm,
      });

      if (result.status !== 'success') {
        throw new Error(result.error || 'Transkription fehlgeschlagen');
      }

      const text     = normalizeHejVi(result.transcript || '');
      const lang     = result.language   || '';
      // Segment-Texte ebenfalls normalisieren — diese werden als transcript_segments
      // ans Backend gesendet und direkt für SRT-Timing verwendet (nie durch normalizeText).
      const segments = (result.segments || []).map(seg => ({
        ...seg,
        text: normalizeHejVi(seg.text || ''),
      }));

      setTranscriptPreview(text);
      setTranscriptPreviewEdited(text);
      setTranscriptPreviewLanguage(lang);
      setTranscriptPreviewSegments(segments);
      setShowTranscriptReview(true);
    } catch (err) {
      setError(err.message || 'Transkription fehlgeschlagen.');
    } finally {
      setIsTranscriptLoading(false);
    }
  };

  // Videoton-Modus + Script-Vorschläge:
  // Transkribiert das Video zuerst via /transcribe-video, speichert das Ergebnis im
  // State (videoToneTranscript / videoToneLanguage) und generiert dann direkt
  // Script-Vorschläge basierend auf dem Transkript.
  // handleSubmit verwendet den gecachten Transcript — kein zweiter Whisper-Call.
  const handleTranscriptForSuggestions = async () => {
    setIsTranscriptLoading(true);
    setIsLoadingScripts(true);
    setError('');
    try {
      const transcribeForm = new FormData();
      transcribeForm.append('video', formData.mediaFile);

      // makeAuthenticatedRequest: auto-refresh + retry bei 401 (kein raw fetch)
      const result = await makeAuthenticatedRequest('/transcribe-video', {
        method: 'POST',
        body: transcribeForm,
      });

      if (result.status !== 'success') {
        throw new Error(result.error || 'Transkription fehlgeschlagen');
      }

      const transcript   = normalizeHejVi(result.transcript || '');
      const detectedLang = result.language   || currentLanguage;

      // Transkript cachen — handleSubmit und handleScriptRetry nutzen dies
      setVideoToneTranscript(transcript);
      setVideoToneLanguage(detectedLang);

      // Script-Vorschläge aus Transkript generieren
      const sugData    = await generateScriptSuggestions(formData.name || '', transcript, detectedLang);
      let finalOptions = sugData?.options || [];
      if (hasFeature('super_admin') && transcript) {
        finalOptions = [...finalOptions, transcript]; // Original-Transkript als letzte Option
      }
      setScriptOptions(finalOptions);
      setShowScriptSelection(true);
    } catch (err) {
      setError(err.message || 'Transkription fehlgeschlagen.');
    } finally {
      setIsTranscriptLoading(false);
      setIsLoadingScripts(false);
    }
  };

  const handleNext = () => {

    if (step === 1) {
      // Dokument-Modus: ggf. Vorschau-Zwischenschritt
      if (formData.isDocumentMode) {
        setError('');
        setStepError('');
        if (canShowDocPreview) {
          handleDocPreview();   // zeigt Zwischenschritt an
        } else {
          handleSubmit();       // direkt erstellen (kein Flag, kein Admin)
        }
        return;
      }
      // Foto/Video → Step 2 (Beschreibung + Toggles)
      setError('');
      setStepError('');
      setStep(2);
      return;
    }

    if (step === 2) {
      // Originalton-Modus: kein Transkript/Script-Review → direkt erstellen
      if (formData.useOriginalAudio) {
        setError('');
        setStepError('');
        handleSubmit();
        return;
      }
      // SuperAdmin + Videoton + Exakt → Transkript-Review (Bearbeitung vor Submit)
      if (userGroup === 0 && formData.useVideoTranscription && formData.useExactText) {
        handleTranscriptPreview();
        return;
      }
      // Videoton + Script-Review: erst transkribieren, dann Vorschläge zeigen
      // (Transkript bereits gecacht → Vorschläge direkt zeigen ohne erneuten Whisper-Call)
      if (formData.useVideoTranscription && enableScriptReview && !formData.useExactText && !selectedScript) {
        if (videoToneTranscript) {
          // Transkript bereits vorhanden → direkt zur Auswahl
          setShowScriptSelection(true);
          if (scriptOptions.length === 0) fetchScriptSuggestions();
        } else {
          // Transkript noch nicht vorhanden → Whisper + Vorschläge
          handleTranscriptForSuggestions();
        }
        return;
      }
      // Normaler Script-Review (ohne Videoton)
      if (enableScriptReview && !formData.useExactText && !formData.useVideoTranscription && !selectedScript) {
        setShowScriptSelection(true);
        if (scriptOptions.length === 0) fetchScriptSuggestions();
        return;
      }
      // Standard → direkt erstellen
      setError('');
      setStepError('');
      handleSubmit();
      return;
    }
  };

  const handleBack = () => {
      if (step > 1) {
        setStepError('');
        setError('');
        setStep(step - 1);
      } else {
        // Wenn wir im ersten Schritt sind (oder abbrechen wollen):
        // Prüfen, ob wir eine Rücksprung-Adresse haben (z.B. vom CourseBuilder)
        if (location.state && location.state.returnUrl) {
            navigate(location.state.returnUrl);
        } else {
            navigate('/profile'); // Fallback, falls kein Ziel angegeben wurde
        }
      }
    };

  const validateStep = (currentStep) => {
    switch(currentStep) {
      case 1: // Upload (Foto/Video oder Dokument)
        if (formData.isDocumentMode) {
          if (!formData.documentFile) {
            setStepError(t('pleaseSelectDocument') || 'Bitte wähle ein Dokument aus (PDF, TXT oder DOC).');
            return false;
          }
        } else {
          if (!formData.mediaFile) {
            setStepError(t('pleaseSelectMedia') || 'Bitte wähle ein Video oder Foto aus.');
            return false;
          }
        }
        setStepError('');
        return true;
      case 2: // Beschreibung (nur Foto/Video-Modus)
        // O-Ton und Videoton brauchen keine Textbeschreibung
        if (!formData.useVideoTranscription && !formData.useOriginalAudio) {
          if (!formData.description.trim() || formData.description.length < 10) {
            setStepError(t('descTooShort') || 'Bitte gib eine Beschreibung ein (min. 10 Zeichen).');
            return false;
          }
        }
        setStepError('');
        return true;
      default:
        setStepError('');
        return true;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset Transkriptions-Toggle wenn neue Datei gewählt
    setFormData(prev => ({ ...prev, useVideoTranscription: false, useExactText: false, useOriginalAudio: false }));
    setShowTranscriptReview(false);
    setTranscriptPreview('');
    setTranscriptPreviewEdited('');
    setTranscriptPreviewLanguage('');
    setTranscriptPreviewSegments([]);
    // Gecachtes Videoton-Transkript verwerfen
    setVideoToneTranscript('');
    setVideoToneLanguage('');

    // Basic Validation
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      setError(t('invalidFileType') || 'Please upload a valid video or image.');
      return;
    }

    // Size limit check (e.g. 100MB for video/image)
    const maxSize = (window.HEJVI_SETTINGS?.maxMediaUploadMB ?? 100) * 1024 * 1024; 
    if (file.size > maxSize) {
      setError(t('fileTooLarge') || `File is too large. Maximum size is ${window.HEJVI_SETTINGS?.maxMediaUploadMB ?? 100}MB.`);
      return;
    }

    // Create Preview
    const previewUrl = URL.createObjectURL(file);
    
    setFormData(prev => ({
      ...prev,
      mediaFile: file,
      mediaType: isVideo ? 'video' : 'image',
      mediaPreviewUrl: previewUrl,
      // Sicherstellen, dass Dokument-Modus deaktiviert
      isDocumentMode: false,
      documentFile: null,
      documentFileName: null,
      documentMimeType: null,
    }));
    setError('');
    setStepError('');
  };

  // NEU: Handler für Dokument-Upload
  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Typ-Validierung
    if (!DOCUMENT_MIME_TYPES.includes(file.type)) {
      setError(t('invalidDocumentType') || 'Bitte lade eine gültige Datei hoch: PDF, TXT, DOC/DOCX, PPT/PPTX oder XLS/XLSX.');
      return;
    }

    // Größenprüfung: Limit zur Laufzeit lesen, damit settings.js bereits geladen ist
    const DOCUMENT_MAX_SIZE = (window.HEJVI_SETTINGS?.maxDocumentUploadMB ?? 10) * 1024 * 1024;
    if (file.size > DOCUMENT_MAX_SIZE) {
      setError(t('documentTooLarge') || `Die Datei ist zu groß. Maximal ${DOCUMENT_MAX_SIZE / 1024 / 1024} MB erlaubt.`);
      return;
    }

    setFormData(prev => ({
      ...prev,
      documentFile: file,
      documentFileName: file.name,
      documentMimeType: file.type,
      isDocumentMode: true,
      // Medien-Felder leeren
      mediaFile: null,
      mediaType: null,
      mediaPreviewUrl: null,
    }));
    setError('');
    setStepError('');
  };

  // NEU: Dokument entfernen
  const handleRemoveDocument = () => {
    setFormData(prev => ({
      ...prev,
      documentFile: null,
      documentFileName: null,
      documentMimeType: null,
      isDocumentMode: false,
    }));
    // Input zurücksetzen
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
    setError('');
  };

  // NEU: Medium entfernen
  const handleRemoveMedia = () => {
    if (formData.mediaPreviewUrl) {
      URL.revokeObjectURL(formData.mediaPreviewUrl);
    }
    setFormData(prev => ({
      ...prev,
      mediaFile: null,
      mediaType: null,
      mediaPreviewUrl: null,
      useExactText: false,
      useVideoTranscription: false,
      useOriginalAudio: false,
    }));
    setShowTranscriptReview(false);
    setTranscriptPreview('');
    setTranscriptPreviewEdited('');
    setTranscriptPreviewLanguage('');
    setTranscriptPreviewSegments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
  };

  // --- SCRIPT SELECTION HANDLERS ---

  const handleScriptSelect = (text) => {
    const cleanText = stripScriptPrefix(text);
    setSelectedScript(cleanText);
    setShowScriptSelection(false);
    // Script gewählt → direkt erstellen (kein Review-Step mehr).
    // WICHTIG: cleanText als Override übergeben — setSelectedScript ist async,
    // handleSubmit würde sonst den alten (null) selectedScript-State lesen.
    handleSubmit({ selectedScript: cleanText });
  };

  const handleScriptRetry = () => {
    if (scriptRetryCount >= 3) {
        alert(t('maxRetriesReached') || "Bitte präzisiere deine Beschreibung. Keine weiteren Versuche möglich.");
        // Zurück zum Editor (Step 3), um Beschreibung zu ändern
        setShowScriptSelection(false); 
        setScriptRetryCount(0);
        setScriptOptions([]);
        return;
    }

    setScriptRetryCount(prev => prev + 1);
    fetchScriptSuggestions(); // Neue Variante anfordern
  };

  const handleSubmit = async (overrides = {}) => {
    setIsProcessing(true);
    setError('');

    try {
      const data = new FormData();

      // --- SECURITY: INPUTS BEREINIGEN ---
      // Name wird vom Backend (KI) generiert — kein Frontend-Name mehr
      const cleanDesc = sanitizeInput(formData.description);

      const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');
      // getValidToken() prüft Expiry, refresht proaktiv (60s vor Ablauf) und liest
      // immer frisch aus localStorage — verhindert stale React-State-Problem bei
      // längerem Tab-Inaktivität vor dem Submit.
      const authToken = await getValidToken();

      // -----------------------------------------------------------------------
      // Video-Transkription – läuft während des Processing-Screens
      // Superadmin (group=0) mit Exakt-Modus: Transkript wurde bereits in Step 3.5
      // geholt und ggf. bearbeitet → kein zweiter Whisper-Call nötig.
      // useOriginalAudio: Whisper + Audio-Extraktion läuft komplett im Backend →
      // kein Frontend-Whisper-Call. Backend erhält nur use_original_audio=1.
      // -----------------------------------------------------------------------
      if (formData.useOriginalAudio && formData.mediaType === 'video') {
        // Originalton-Modus: Backend übernimmt Whisper + Audio-Extraktion
        data.append('use_original_audio', '1');
        // Beschreibung optional mitsenden (für Suchbegriffe / DB)
        if (formData.description.trim()) {
          data.append('description', formData.description.trim());
        }
      } else if (formData.useVideoTranscription && formData.mediaType === 'video' && formData.mediaFile) {
        if (userGroup === 0 && formData.useExactText && transcriptPreviewEdited) {
          // Superadmin-Pfad: bearbeitetes Transkript aus Step 3.5 verwenden
          data.append('detected_language',    transcriptPreviewLanguage);
          data.append('description',          transcriptPreviewEdited);
          data.append('video_transcript',     transcriptPreviewEdited);
          // Wenn Transkript bearbeitet wurde → keine Whisper-Segmente (Timing passt nicht mehr)
          const segsToSend = transcriptPreviewEdited === transcriptPreview
            ? JSON.stringify(transcriptPreviewSegments)
            : '[]';
          data.append('transcript_segments',  segsToSend);
          data.append('use_exact_text',       '1');
          data.append('edited_transcript',    transcriptPreviewEdited);
        } else if (videoToneTranscript) {
          // Videoton + Script-Vorschläge: Transkript wurde bereits in handleTranscriptForSuggestions
          // geholt — kein zweiter Whisper-Call nötig.
          data.append('detected_language',    videoToneLanguage);
          data.append('description',          videoToneTranscript);
          data.append('video_transcript',     '');
          data.append('transcript_segments',  '[]');
          data.append('use_exact_text',       '0');
        } else {
          // Standard-Pfad: Whisper während des Submits aufrufen
          const transcribeForm = new FormData();
          transcribeForm.append('video', formData.mediaFile);

          // makeAuthenticatedRequest: auto-refresh + retry bei 401 (kein raw fetch)
          const transcribeResult = await makeAuthenticatedRequest('/transcribe-video', {
            method: 'POST',
            body: transcribeForm,
          });

          if (transcribeResult.status !== 'success') {
            throw new Error(transcribeResult.error || 'Transkription fehlgeschlagen');
          }

          const transcript   = normalizeHejVi(transcribeResult.transcript || '');
          const detectedLang = transcribeResult.language   || '';
          // Segment-Texte normalisieren — werden direkt für SRT-Timing verwendet
          const segments     = (transcribeResult.segments || []).map(seg => ({
            ...seg,
            text: normalizeHejVi(seg.text || ''),
          }));

          data.append('detected_language', detectedLang);

          if (formData.useExactText) {
            data.append('description',         transcript);
            data.append('video_transcript',    transcript);
            data.append('transcript_segments', JSON.stringify(segments));
            data.append('use_exact_text', '1');
          } else {
            data.append('description',         transcript);
            data.append('video_transcript',    '');
            data.append('transcript_segments', '[]');
            data.append('use_exact_text', '0');
          }
        }
      }
      // -----------------------------------------------------------------------

      data.append('name', ''); // Name wird vom Backend generiert
      // Vorschau-Overrides: editierter Name/Beschreibung aus Zwischenschritt
      if (overrides.doc_name_override) {
        data.append('doc_name_override', overrides.doc_name_override);
      }
      if (overrides.doc_desc_override) {
        data.append('doc_desc_override', overrides.doc_desc_override);
      }
      // Beschreibungsfeld nur anhängen wenn NICHT useVideoTranscription und NICHT useOriginalAudio
      // (oben bereits korrekt gesetzt)
      if (!formData.useVideoTranscription && !formData.useOriginalAudio) {
        data.append('description', formData.description || '');
      }
      // --- ÄNDERUNG: Skript nur senden, wenn Exact Text AUS ist ---
      // overrides.selectedScript hat Vorrang: kommt von handleScriptSelect, das
      // setSelectedScript (async) + handleSubmit synchron hintereinander aufruft.
      // Ohne Override würde selectedScript hier noch null sein (State noch nicht resolved).
      const effectiveScript = overrides.selectedScript ?? selectedScript;
      if (effectiveScript && !formData.useExactText && !formData.isDocumentMode) {
          data.append('selected_script', effectiveScript);
      }
      // ------------------------------------------------------------
      if (user && user.id) {
        data.append('userid', user.id);
      }
      // use_exact_text nur anhängen wenn nicht bereits von Transkriptions-Block gesetzt
      if (!formData.useVideoTranscription) {
        data.append('use_exact_text', formData.useExactText ? '1' : '0');
      }

      // Aktuelle Sprache mitsenden (Fallback auf 'de')
      // Bei Transkription überschreibt detected_language die source_language im VideoBuilder
      data.append('source_language', currentLanguage || 'de');
      
      // NEU: Dokument-Modus Flag
      data.append('document_mode', formData.isDocumentMode ? '1' : '0');

      // Datei immer als 'media_file' senden – der bestehende Controller-Validator
      // erwartet dieses Feld. Das Flag document_mode=1 signalisiert VideoBuilder
      // intern den Dokumenten-Verarbeitungspfad.
      if (formData.isDocumentMode && formData.documentFile) {
        data.append('media_file', formData.documentFile);
      } else if (formData.mediaFile) {
        data.append('media_file', formData.mediaFile);
      }

      // --- NEU: CONTENT TYPE LOGIK (NUR ADMINS) ---
      // Wenn Reaktionsvideo aktiv -> Typ 4, sonst Standard (1)
      const contentType = formData.isReactionVideo ? '4' : '1';
      data.append('content_type', contentType);
      // --------------------------------------------
      
      data.append('auth_token', authToken);
      data.append('progress_token', progressToken);
      // Für Admins: assign_to_general Flag mitsenden (Normal-User: immer 1)
      data.append('assign_to_general', (!isAdminUser || assignToGeneral) ? '1' : '0');

      console.log("Transferring Data to api: " + currentLanguage || 'de');
      console.log("Name: " + formData.name);
      console.log("Document mode: " + formData.isDocumentMode);
      

      // makeAuthenticatedRequest unterstützt FormData und macht bei 401 automatisch
      // einen Token-Refresh + Retry — kein rohes fetch() mit statischem Token nötig.
      let result;
      try {
        result = await makeAuthenticatedRequest('/create-element', {
          method: 'POST',
          body: data,
        });
      } catch (fetchErr) {
        if (fetchErr?.status === 504 || fetchErr?.status === 502) {
          const timeoutErr = new Error('TIMEOUT');
          timeoutErr.isTimeout = true;
          throw timeoutErr;
        }
        const msg = fetchErr?.message || '';
        if (msg.includes('Content rejected') || msg.includes('moderation')) {
          throw new Error(t('errorContentRejected') || 'Dein Inhalt wurde abgelehnt, da er gegen die Sicherheitsrichtlinien verstößt.');
        }
        throw fetchErr;
      }

      // makeAuthenticatedRequest wirft bereits bei !res.ok — hier nur status=error prüfen
      if (result?.status === 'error') {
        let errorMsg = result.message || 'Upload fehlgeschlagen';
        if (errorMsg.includes('Content rejected') || errorMsg.includes('moderation')) {
          errorMsg = t('errorContentRejected') || 'Dein Inhalt wurde abgelehnt, da er gegen die Sicherheitsrichtlinien verstößt.';
        }
        throw new Error(errorMsg);
      }

      // Fast-lane: asynchrone Verarbeitung gestartet — sofort Erfolgsscreen zeigen
      if (result?.status === 'queued') {
        setCreatedElementData({
          ...result,
          is_queued: true,
          hash_id: result.hash_id,
          qr_direct_url: result.qr_direct_url,
          element_id: result.element_id,
        });
        setIsProcessing(false);
        setStep(6);
        return;
      }

      if (result.status === 'success') {
        const isPendingApproval = result.pending_approval === true;

        // --- Workflow 1: Rückkehr zu einem bestehenden Kurs (von CourseDetail) ---
        // Bei pending_approval NICHT automatisch weiterleiten — User soll Pending-Screen sehen
        const returnToCourseHash = sessionStorage.getItem('hejvi_return_to_course');
        if (returnToCourseHash && !isPendingApproval) {
            sessionStorage.removeItem('hejvi_return_to_course');
            const newElementId = result.videoId || result.id;

            const newElementForCourse = {
                id: newElementId,
                hash_id: result.hash_id || null,
                name: result.generated_name || '',
                description: formData.description,
                url_thumbnail: result.url_thumbnail || formData.mediaPreviewUrl || null,
                type: 1,
                content_type: 1,
                progress: 0,
                duration: result.duration || 0
            };
            sessionStorage.setItem(
                `hejvi_new_element_for_course_${returnToCourseHash}`,
                JSON.stringify(newElementForCourse)
            );

            const tryAddToCollection = async () => {
                try {
                    await addElementToCollection(returnToCourseHash, newElementId);
                    sessionStorage.setItem('hejvi_course_updated', returnToCourseHash);
                } catch (err) {
                    console.warn('add-element request failed:', err);
                }
            };

            tryAddToCollection();

            navigate(`/course/${returnToCourseHash}`, {
                state: { newElementAdded: true, newElementId }
            });
            return;
        }

        // --- Workflow 2: Rückkehr zum CourseBuilder (Draft vorhanden) ---
        // Bei pending_approval ebenfalls Pending-Screen zeigen
        const draft = sessionStorage.getItem('hejvi_course_draft');
        if (draft && !isPendingApproval) {
            const newElementForBuilder = {
                id: result.videoId || result.id,
                name: formData.name,
                description: formData.description,
                url_thumbnail: result.url_thumbnail || formData.mediaPreviewUrl,
                type: 1,
                content_type: 1
            };
            
            sessionStorage.setItem('hejvi_created_element', JSON.stringify(newElementForBuilder));
            navigate('/create-course');
            return;
        }
        // ---------------------------

        setCreatedElementData(result);
        setIsProcessing(false);
        setStep(6); 
      }

    } catch (err) {
      console.error("Upload failed", err);
      setIsProcessing(false);
      if (err.isTimeout || err.message === 'TIMEOUT') {
        setUploadErrorType('timeout');
        setError('timeout');
      } else {
        setUploadErrorType('general');
        setError(err.message || (t('uploadFailed') || 'Upload fehlgeschlagen. Bitte erneut versuchen.'));
      }
    }
  };

  // --- NIIMBOT DRUCKER FUNKTIONEN ---

  // ── Batch sessionStorage persistence
  useEffect(() => {
    const saved = sessionStorage.getItem('hejvi_batchMode');
    const savedType = sessionStorage.getItem('hejvi_batchType') || 'document';
    if (saved === 'true') { setBatchMode(true); setBatchType(savedType); }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('hejvi_batchMode', batchMode ? 'true' : 'false');
    sessionStorage.setItem('hejvi_batchType', batchType);
  }, [batchMode, batchType]);

  const loadImageToCanvas = useCallback((imageUrl) => {
    return new Promise((resolve, reject) => {
      const LABEL_W = 400;
      const LABEL_H = 240;
      const canvas = printCanvasRef.current;
      canvas.width  = LABEL_W;
      canvas.height = LABEL_H;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, LABEL_W, LABEL_H);

      const drawQR = (img) => {
        const padding = 10;
        const size = LABEL_H - padding * 2;
        const x = Math.round((LABEL_W - size) / 2);
        const y = padding;
        ctx.drawImage(img, x, y, size, size);
        resolve(canvas);
      };

      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => drawQR(img);

      img.onerror = () => {
        fetch(imageUrl)
          .then(res => res.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const img2 = new Image();
            img2.onload = () => { drawQR(img2); URL.revokeObjectURL(blobUrl); };
            img2.onerror = () => reject(new Error('Bild konnte nicht geladen werden.'));
            img2.src = blobUrl;
          })
          .catch(reject);
      };

      img.src = imageUrl;
    });
  }, []);

  const connectPrinter = async () => {
    setPrintStatus('connecting');
    try {
      const { NiimbotBluetoothClient } = await import('@mmote/niimbluelib');
      const client = new NiimbotBluetoothClient();
      await client.connect();
      setPrinterClient(client);
      setPrinterConnected(true);
      setPrintStatus('idle');
    } catch (err) {
      console.error('[Niimbot] Verbindung fehlgeschlagen:', err);
      setPrintStatus('error');
    }
  };

  const printQRLabel = async () => {
    if (!createdElementData?.qr_direct_url) return;

    let lib;
    try {
      lib = await import('@mmote/niimbluelib');
    } catch (err) {
      console.error('[Niimbot] Library konnte nicht geladen werden:', err);
      setPrintStatus('error');
      return;
    }

    const { NiimbotBluetoothClient, ImageEncoder, Abstraction, B1PrintTask } = lib;

    let client = printerClient;
    if (!printerConnected || !client) {
      setPrintStatus('connecting');
      try {
        client = new NiimbotBluetoothClient();

        client.on('disconnect', () => {
          setPrinterConnected(false);
          setPrinterClient(null);
          setPrintStatus('idle');
        });

        await client.connect();
        setPrinterClient(client);
        setPrinterConnected(true);
        console.log('[Niimbot] Verbunden');
      } catch (err) {
        console.error('[Niimbot] Verbindung fehlgeschlagen:', err);
        setPrintStatus('error');
        return;
      }
    }

    setPrintStatus('printing');
    try {
      const canvas = await loadImageToCanvas(createdElementData.qr_direct_url);

      const labelProps = {
        width:          canvas.width,
        height:         canvas.height,
        printDirection: 'left',
      };

      const encoded = ImageEncoder.encodeCanvas(canvas, labelProps);

      const abstraction = new Abstraction(client);
      const task = new B1PrintTask(abstraction);
      await task.printInit(encoded, 1);
      await task.printPage(encoded, 1);
      await task.waitForFinished();

      setPrintStatus('done');
      console.log('[Niimbot] Druck erfolgreich!');
    } catch (err) {
      console.error('[Niimbot] Druckfehler:', err);
      setPrintStatus('error');
    }
  };

  // ── Thumbnail-URLs für Foto-Batch (erstellt + räumt auf bei Dateiänderung)
  useEffect(() => {
    if (batchType !== 'photo' || batchFiles.length === 0) {
      setPhotoThumbs([]);
      return;
    }
    const urls = batchFiles.map(f => URL.createObjectURL(f));
    setPhotoThumbs(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [batchFiles, batchType]);

  // Slide-Dauer schätzen: leer → 3.5s, Text → Wörter / 2.5 Wörter/Sek (TTS-Richtwert)
  const estimateSlideDuration = (text) => {
    if (!text?.trim()) return 3.5;
    const words = text.trim().split(/\s+/).length;
    return Math.max(2.0, Math.round((words / 2.5) * 10) / 10);
  };

  // ── Batch-Datei-Reihenfolge per Drag & Drop ändern
  const reorderFiles = (fromIdx, toIdx) => {
    if (fromIdx === null || toIdx === null || fromIdx === toIdx) return;
    const doReorder = (arr) => {
      const a = [...arr];
      const [moved] = a.splice(fromIdx, 1);
      a.splice(toIdx, 0, moved);
      return a;
    };
    setBatchFiles(prev => doReorder(prev));
    setSlideTexts(prev => doReorder(prev));
  };

  // Touch-Drag: touchstart speichert Ausgangsindex
  const handleDragTouchStart = (e, index) => {
    e.stopPropagation();
    touchDragRef.current = { active: true, fromIndex: index, toIndex: index };
    setDragIndex(index);
  };

  // Touch-Drag: touchmove ermittelt Ziel per elementFromPoint
  const handleDragTouchMove = (e) => {
    if (!touchDragRef.current.active) return;
    e.preventDefault(); // Scroll unterdrücken während Drag
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const item = el?.closest('[data-drag-index]');
    if (item) {
      const toIdx = parseInt(item.dataset.dragIndex, 10);
      if (!isNaN(toIdx) && toIdx !== touchDragRef.current.toIndex) {
        touchDragRef.current.toIndex = toIdx;
        setDragOverIndex(toIdx);
      }
    }
  };

  // Touch-Drag: touchend führt Reorder aus und räumt auf
  const handleDragTouchEnd = () => {
    if (touchDragRef.current.active) {
      reorderFiles(touchDragRef.current.fromIndex, touchDragRef.current.toIndex);
    }
    touchDragRef.current = { active: false, fromIndex: null, toIndex: null };
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // ── handleBatchSubmit
  const handleBatchSubmit = async () => {
    if (batchFiles.length === 0) return;
    setBatchSubmitting(true);
    const fd = new FormData();
    fd.append('batch_type', batchType);
    fd.append('source_language', formData.sourceLanguage || 'de');

    // Einstellungen: assign_to_general gilt für alle Typen (Normal-User immer 1)
    fd.append('assign_to_general', (!isAdminUser || batchAssignToGeneral) ? '1' : '0');

    if (batchType === 'photo') {
      fd.append('description', batchDescription.trim());
      fd.append('use_exact_text', batchUseExactText ? '1' : '0');
      // Per-Slide-Narrationstexte: slide_text_0, slide_text_1, ...
      // Backend liest diese für variable TTS-Dauer pro Slide.
      // Kein Text → Backend verwendet Standarddauer (3.5s), kein TTS für diesen Slide.
      slideTexts.forEach((text, i) => {
        fd.append('slide_text_' + i, text.trim());
      });
      fd.append('slide_count', String(batchFiles.length));
    }
    if (batchType === 'video') {
      fd.append('use_exact_text', batchUseExactText ? '1' : '0');
      if (canUseOriginalAudio) fd.append('use_original_audio', batchUseOriginalAudio ? '1' : '0');
    }
    // document: nur assign_to_general (bereits oben gesetzt)
    batchFiles.forEach((file) => fd.append('files[]', file, file.name));
    try {
      const authToken = await getValidToken() || '';
      const baseUrl = appConfig.apiBaseUrl.replace(/\/$/, '');
      // auth_token im FormData mitsenden — index.php liest $_POST['auth_token']
      // und setzt daraus den Authorization-Header (Zeile 17-21 index.php)
      if (authToken) fd.append('auth_token', authToken);
      const resp = await fetch(`${baseUrl}/create-element-batch`, {
        method: 'POST',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok || data.status === 'error') {
        setError(data.message || 'Batch-Upload fehlgeschlagen.');
        setBatchSubmitting(false);
        return;
      }
      sessionStorage.removeItem('hejvi_batchMode');
      sessionStorage.removeItem('hejvi_batchType');
      setBatchResult(data);
      setStep(6);
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setBatchSubmitting(false);
    }
  };

  // ── downloadBatchQRPDF
  const downloadBatchQRPDF = async (jobs) => {
    if (!jobs || !jobs.length) return;
    let QRCode;
    try { const mod = await import('qrcode'); QRCode = mod.default || mod; } catch (e) { return; }
    const qrDataUrls = await Promise.all(jobs.map(async (job) => {
      if (!job.qr_direct_url) return '';
      try { return await QRCode.toDataURL(job.qr_direct_url, { width: 200, margin: 1 }); } catch (e) { return ''; }
    }));
    const win = window.open('', '_blank', 'width=794,height=1123');
    if (!win) { alert('Popup-Blocker aktiv.'); return; }
    const itemsHtml = jobs.map((job, i) => {
      const name = (job.display_name || job.original_name || `Element ${i+1}`).replace(/</g,'&lt;').substring(0,40);
      return `<div class="qi"><img src="${qrDataUrls[i]||''}" /><p>${name}</p></div>`;
    }).join('');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>HejVi QR</title>
<style>body{margin:0;padding:16px;font-family:Arial,sans-serif}h1{font-size:16px;margin-bottom:16px}
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.qi{text-align:center;border:1px solid #e2e8f0;border-radius:8px;padding:12px 8px}
.qi img{width:130px;height:130px;display:block;margin:0 auto 8px}
.qi p{font-size:11px;color:#374151;margin:0;word-break:break-word}
@media print{body{padding:8px}}</style></head><body>
<h1>HejVi QR-Codes (${jobs.length})</h1><div class="grid">${itemsHtml}</div>
<script>window.onload=function(){window.print()}</script></body></html>`);
    win.document.close();
  };

  // ── printAllBatchLabels
  const printAllBatchLabels = async (jobs) => {
    if (!jobs || !jobs.length) return;
    setBatchPrinting(true);
    let lib;
    try { lib = await import('@mmote/niimbluelib'); } catch (err) { setBatchPrinting(false); return; }
    const { NiimbotBluetoothClient, ImageEncoder, Abstraction, B1PrintTask } = lib;
    let client = printerClient;
    if (!printerConnected || !client) {
      try {
        client = new NiimbotBluetoothClient();
        client.on('disconnect', () => { setPrinterConnected(false); setPrinterClient(null); });
        await client.connect(); setPrinterClient(client); setPrinterConnected(true);
      } catch (err) { setPrintStatus('error'); setBatchPrinting(false); return; }
    }
    for (let i = 0; i < jobs.length; i++) {
      if (!jobs[i].qr_direct_url) continue;
      setBatchPrintIdx(i);
      try {
        const canvas = await loadImageToCanvas(jobs[i].qr_direct_url);
        const enc = ImageEncoder.encodeCanvas(canvas, { width: canvas.width, height: canvas.height, printDirection: 'left' });
        const task = new B1PrintTask(new Abstraction(client));
        await task.printInit(enc, 1); await task.printPage(enc, 1); await task.waitForFinished();
        await new Promise(r => setTimeout(r, 800));
      } catch (err) { console.error('[Niimbot]', i+1, err); }
    }
    setBatchPrinting(false); setBatchPrintIdx(-1); setPrintStatus('done');
  };

  // --- RENDER STEPS ---

  // nameInputRef entfernt — neuer Workflow hat kein Namensfeld

  // Fokus auf das Namensfeld setzen wenn Schritt 1 aktiv ist
  // useEffect für nameInput-Focus entfernt (kein Namensfeld)
  // Sprachlade-Guard: kein Render vor geladenem Wörterbuch (Lazy-Loading)
  if (isLoadingLang) return null;

  const renderStep2_Media = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep1Title_new') || 'Lade deinen Inhalt hoch'}</h2>
      <p className="ec-subtitle">{t('createStep1Sub_new') || 'Foto/Video aufnehmen, aus Bibliothek laden oder Dokument hinzufügen.'}</p>
      
      {/* --- NEU: TAB SWITCHER --- */}
      <div className="ec-upload-tabs">
        <button
          className={`ec-upload-tab ${!batchMode && uploadMode === 'media' ? 'active' : ''}`}
          onClick={() => handleUploadModeChange('media')}
          type="button"
        >
          <span className="ec-upload-tab-icon">🎬</span>
          <span>{t('photoVideo') || 'Foto / Video'}</span>
        </button>
        <button
          className={`ec-upload-tab ${!batchMode && uploadMode === 'document' ? 'active' : ''}`}
          onClick={() => handleUploadModeChange('document')}
          type="button"
        >
          <span className="ec-upload-tab-icon">📄</span>
          <span>{t('document') || 'Dokument'}</span>
        </button>
        <button
          className={`ec-upload-tab ${batchMode ? 'active' : ''}`}
          onClick={() => { setBatchMode(true); setUploadMode('media'); setBatchFiles([]); setSlideTexts([]); setBatchResult(null); setBatchStep(1); }}
          type="button"
        >
          <span className="ec-upload-tab-icon">📦</span>
          <span>Mehrere Dateien</span>
        </button>
      </div>
      {/* ----------------------- */}

      {/* Inline-Fehler wenn kein Medium / Dokument gewählt */}
      {stepError && (
        <div style={{
          margin: '8px 0 4px', padding: '10px 14px',
          background: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: '10px', color: '#DC2626',
          fontSize: '13px', fontWeight: '500',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span>⚠️</span><span>{stepError}</span>
        </div>
      )}

      {batchMode ? (
        /* ── BATCH-UPLOAD-MODUS ── */
        <div style={{ marginTop: 12, paddingBottom: 16 }}>

          {/* ── SCHRITT 1: Typ-Auswahl + Datei-Picker ── */}
          {batchStep === 1 && (
            <>
              {/* Batch-Typ Auswahl */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[
                  { key: 'document', label: '📄 Dokumente' },
                  { key: 'photo',    label: '🖼️ Fotos' },
                  { key: 'video',    label: '🎬 Videos' },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => { setBatchType(key); setBatchFiles([]); setSlideTexts([]); setBatchUseOriginalAudio(false); setBatchUseExactText(false); setBatchDescription(''); setBatchStep(1); }} type="button"
                    style={{
                      flex: 1, padding: '10px 6px', borderRadius: 10, border: 'none',
                      background: batchType === key ? '#ede9fe' : '#f1f5f9',
                      color: batchType === key ? '#6d28d9' : '#374151',
                      fontWeight: batchType === key ? 700 : 400,
                      fontSize: 12, cursor: 'pointer', textAlign: 'center',
                      boxShadow: batchType === key ? 'inset 0 0 0 2px #a78bfa' : 'none',
                    }}
                  >{label}</button>
                ))}
              </div>

              {/* Multi-File-Picker */}
              <div className="ec-upload-area" style={{ minHeight: 72, cursor: 'pointer' }}
                onClick={() => { if (dragIndex === null && !touchDragRef.current.active) batchFileInputRef.current?.click(); }}>
                {batchFiles.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 16px', color: '#94a3b8' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 4 }}>
                      {batchType === 'document' && 'PDF, DOCX, XLSX oder PPTX auswählen'}
                      {batchType === 'photo'    && 'Fotos auswählen (werden zu Video)'}
                      {batchType === 'video'    && 'Videos auswählen'}
                    </div>
                    <div style={{ fontSize: 12 }}>Bis zu {batchType === 'photo' ? 30 : 15} Dateien gleichzeitig</div>
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px', width: '100%' }}>
                    <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>{batchFiles.length} Datei{batchFiles.length !== 1 ? 'en' : ''} ausgewählt</span>
                      {batchType === 'photo' && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: batchFiles.length >= 30 ? '#fef2f2' : '#ede9fe',
                          color:      batchFiles.length >= 30 ? '#dc2626'  : '#6d28d9',
                          border:     `1px solid ${batchFiles.length >= 30 ? '#fecaca' : '#c4b5fd'}`,
                        }}>
                          {batchFiles.length}/30{batchFiles.length >= 30 ? ' — Maximum erreicht' : ''}
                        </span>
                      )}
                      <span style={{ fontWeight: 400, color: '#94a3b8' }}>(Klicken zum Ändern)</span>
                    </div>
                    {/* Drag-Hinweis nur bei Foto (Slideshow-Reihenfolge) */}
                    {batchType === 'photo' && (
                      <div style={{ fontSize: 10, color: '#a78bfa', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>↕</span>
                        <span>Reihenfolge per Drag & Drop anpassen — bestimmt die Reihenfolge im Video</span>
                      </div>
                    )}
                    {batchFiles.map((file, i) => (
                      <div
                        key={i}
                        data-drag-index={i}
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.effectAllowed = 'move';
                          setDragIndex(i);
                          setDragOverIndex(null);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (dragOverIndex !== i) setDragOverIndex(i);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          reorderFiles(dragIndex, i);
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        onDragEnd={(e) => {
                          e.stopPropagation();
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        onTouchStart={(e) => handleDragTouchStart(e, i)}
                        onTouchMove={handleDragTouchMove}
                        onTouchEnd={handleDragTouchEnd}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '5px 0',
                          borderTop: dragOverIndex === i && dragIndex !== i
                            ? '2px solid #7c3aed'
                            : '2px solid transparent',
                          borderBottom: '1px solid #f1f5f9',
                          fontSize: 12, color: '#374151',
                          opacity: dragIndex === i ? 0.35 : 1,
                          cursor: 'grab',
                          userSelect: 'none',
                          touchAction: 'none',
                          transition: 'opacity 0.15s',
                        }}
                      >
                        {/* Positionsnummer */}
                        <span style={{
                          flexShrink: 0, minWidth: 18, height: 18,
                          background: '#ede9fe', color: '#6d28d9',
                          borderRadius: 4, fontSize: 10, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{i + 1}</span>
                        {/* Drag-Handle */}
                        <span style={{ flexShrink: 0, color: '#c4b5fd', fontSize: 14, lineHeight: 1 }}
                          onMouseDown={(e) => e.stopPropagation()}>⠿</span>
                        {/* Datei-Icon */}
                        <span style={{ flexShrink: 0 }}>
                          {file.type?.includes('pdf') ? '📕' : file.type?.includes('word') ? '📘' :
                           file.type?.includes('sheet') || file.type?.includes('excel') ? '📗' :
                           file.type?.includes('presentation') ? '📙' :
                           file.type?.startsWith('image') ? '🖼️' : file.type?.startsWith('video') ? '🎬' : '📄'}
                        </span>
                        {/* Dateiname */}
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        {/* Dateigröße */}
                        <span style={{ flexShrink: 0, color: '#94a3b8' }}>{(file.size/1024/1024).toFixed(1)} MB</span>
                        {/* Löschen */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setBatchFiles(prev => prev.filter((_,j)=>j!==i)); setSlideTexts(prev => prev.filter((_,j)=>j!==i)); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', fontSize:14, padding:'0 2px', flexShrink: 0 }}
                          type="button">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <input ref={batchFileInputRef} type="file" style={{ display: 'none' }} multiple
                accept={batchType==='document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx' : batchType==='photo' ? 'image/*' : 'video/*'}
                onChange={(e) => {
                  const maxFiles = batchType === 'photo' ? 30 : 15;
                  const selected = Array.from(e.target.files || []);
                  const clipped  = selected.slice(0, maxFiles);
                  if (selected.length > maxFiles) {
                    setStepError(
                      `Maximal ${maxFiles} ${batchType === 'photo' ? 'Fotos' : 'Dateien'} erlaubt — die ersten ${maxFiles} wurden übernommen, ${selected.length - maxFiles} ignoriert.`
                    );
                  } else {
                    setStepError('');
                  }
                  if (batchType === 'photo') {
                    setSlideTexts(clipped.map(f => {
                      const prevIdx = batchFiles.findIndex(bf => bf.name === f.name && bf.size === f.size);
                      return prevIdx >= 0 ? (slideTexts[prevIdx] || '') : '';
                    }));
                  }
                  setBatchFiles(clipped);
                  e.target.value = '';
                }} />
            </>
          )}

          {/* ── SCHRITT 2: Einstellungen ── */}
          {batchStep === 2 && (
            <>
              {/* Auswahl-Zusammenfassung (read-only, mit Bearbeiten-Link + Gesamtdauer) */}
              {(() => {
                const totalSec = batchType === 'photo'
                  ? batchFiles.reduce((s, _, i) => { const t = slideTexts[i] || ''; return s + estimateSlideDuration(t) + (t.trim() ? 0.5 : 0); }, 0)
                  : 0;
                const durStr = totalSec < 60
                  ? Math.round(totalSec) + 's'
                  : Math.floor(totalSec / 60) + 'm ' + Math.round(totalSec % 60) + 's';
                return (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>
                        <strong>{batchFiles.length}</strong> {batchType === 'photo' ? 'Foto' : batchType === 'document' ? 'Dokument' : 'Video'}{batchFiles.length !== 1 ? 's' : ''}
                        <span style={{ color: '#94a3b8', marginLeft: 5 }}>
                          {batchType === 'document' ? '📄' : batchType === 'photo' ? '🖼️' : '🎬'}
                        </span>
                      </span>
                      {batchType === 'photo' && (
                        <span style={{ fontSize: 11, color: '#6d28d9', background: '#ede9fe', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>
                          ⏱ ~{durStr}
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => setBatchStep(1)}
                      style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12, cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                      ✏️ Ändern
                    </button>
                  </div>
                );
              })()}

              {/* FOTO: Per-Slide-Narration-Editor */}
              {batchType === 'photo' && (
                <>
                  {/* Slideshow-Beschreibung — ÜBER den Slides damit sie nicht übersehen wird */}
                  <div style={{
                    marginBottom: 14,
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)',
                    border: '1.5px solid #c4b5fd',
                  }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      Beschreibung
                      <span style={{ fontWeight: 400, color: '#a78bfa', fontSize: 11 }}>optional — HejVi generiert daraus den Titel</span>
                    </label>
                    <textarea
                      value={batchDescription}
                      onChange={(e) => setBatchDescription(e.target.value)}
                      placeholder={t('descPlaceholder') || 'Thema oder: worum geht es?…'}
                      rows={2}
                      style={{
                        width: '100%', padding: '9px 12px', borderRadius: 8,
                        border: '1.5px solid #c4b5fd', fontSize: 13,
                        boxSizing: 'border-box', resize: 'vertical',
                        background: '#fff', outline: 'none', fontFamily: 'inherit',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                      onBlur={(e) => e.target.style.borderColor = '#c4b5fd'}
                    />
                  </div>

                  {/* Per-Slide-Karten */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {batchFiles.map((file, i) => {
                      const text = slideTexts[i] || '';
                      const dur  = estimateSlideDuration(text);
                      const hasText = text.trim().length > 0;
                      return (
                        <div key={i} style={{
                          background: '#fff', border: '1px solid',
                          borderColor: hasText ? '#a78bfa' : '#e2e8f0',
                          borderRadius: 12, padding: '10px 12px',
                          boxShadow: hasText ? '0 0 0 2px #ede9fe' : 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}>
                          {/* Header: Thumb + Info + Dauer + ↑↓-Reorder */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            {/* Thumbnail — klickbar → Lightbox */}
                            {photoThumbs[i] ? (
                              <img src={photoThumbs[i]} alt=""
                                onClick={() => setPhotoLightboxIdx(i)}
                                style={{ width: 48, height: 38, objectFit: 'cover', borderRadius: 7, flexShrink: 0, border: '1px solid #e2e8f0', cursor: 'zoom-in' }} />
                            ) : (
                              <div style={{ width: 48, height: 38, borderRadius: 7, background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🖼️</div>
                            )}
                            {/* Position + Dateiname + Dauer */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ flexShrink: 0, minWidth: 20, height: 20, background: '#ede9fe', color: '#6d28d9', borderRadius: 5, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {i + 1}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {file.name}
                                </span>
                              </div>
                              <div style={{ fontSize: 11, color: hasText ? '#7c3aed' : '#94a3b8', marginTop: 2 }}>
                                {hasText ? `⏱ ~${dur + 0.5}s` : 'kein Kommentar'}
                              </div>
                            </div>
                            {/* ↑↓ Reihenfolge-Buttons */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                              <button type="button"
                                disabled={i === 0}
                                onClick={() => reorderFiles(i, i - 1)}
                                style={{ background: i === 0 ? '#f1f5f9' : '#ede9fe', border: 'none', borderRadius: 5, width: 24, height: 22, cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#cbd5e1' : '#6d28d9', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                title="Nach oben">▲</button>
                              <button type="button"
                                disabled={i === batchFiles.length - 1}
                                onClick={() => reorderFiles(i, i + 1)}
                                style={{ background: i === batchFiles.length - 1 ? '#f1f5f9' : '#ede9fe', border: 'none', borderRadius: 5, width: 24, height: 22, cursor: i === batchFiles.length - 1 ? 'default' : 'pointer', color: i === batchFiles.length - 1 ? '#cbd5e1' : '#6d28d9', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                title="Nach unten">▼</button>
                            </div>
                          </div>
                          {/* Narrations-Textarea */}
                          <textarea
                            value={text}
                            onChange={(e) => setSlideTexts(prev => {
                              const arr = [...prev];
                              // Array auf Länge auffüllen falls nötig
                              while (arr.length <= i) arr.push('');
                              arr[i] = e.target.value;
                              return arr;
                            })}
                            placeholder="Kommentar zu diesem Bild (optional)"
                            rows={2}
                            style={{
                              width: '100%', padding: '8px 10px', borderRadius: 8,
                              border: '1px solid', borderColor: hasText ? '#c4b5fd' : '#e2e8f0',
                              fontSize: 13, boxSizing: 'border-box', resize: 'vertical',
                              background: hasText ? '#faf5ff' : '#f8fafc',
                              outline: 'none', fontFamily: 'inherit',
                              transition: 'border-color 0.2s, background 0.2s',
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Text exakt übernehmen */}
                  {canUseExactText && (
                    <div className="ec-toggle-container" style={{ marginTop: 4 }}>
                      <label className="ec-switch">
                        <input type="checkbox" checked={batchUseExactText}
                          onChange={(e) => setBatchUseExactText(e.target.checked)} />
                        <span className="ec-slider"></span>
                      </label>
                      <div className="ec-toggle-label">
                        <strong>{t('useExactTextTitle') || 'Text exakt übernehmen'}</strong>
                        <span style={{ color: '#0369a1' }}>
                          {t('useExactTextDesc') || 'Narrationstexte 1:1 vorlesen — keine KI-Umformulierung.'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* VIDEO: exakter Text + Originalton */}
              {batchType === 'video' && (
                <>
                  {canUseExactText && (
                    <div className="ec-toggle-container">
                      <label className="ec-switch">
                        <input type="checkbox" checked={batchUseExactText}
                          onChange={(e) => setBatchUseExactText(e.target.checked)} />
                        <span className="ec-slider"></span>
                      </label>
                      <div className="ec-toggle-label">
                        <strong>{t('useExactTextTitle') || 'Text exakt übernehmen'}</strong>
                        <span style={{ color: '#0369a1' }}>
                          {t('transcribeExactToggleDesc') || 'Transkription 1:1 übernehmen (synchron zum Bild). Aus: KI erstellt optimiertes Skript aus dem Transkript.'}
                        </span>
                      </div>
                    </div>
                  )}
                  {canUseOriginalAudio && (
                    <div className="ec-toggle-container" style={{ marginTop: 10 }}>
                      <label className="ec-switch">
                        <input type="checkbox" checked={batchUseOriginalAudio}
                          onChange={(e) => setBatchUseOriginalAudio(e.target.checked)} />
                        <span className="ec-slider" style={{ backgroundColor: batchUseOriginalAudio ? '#8B5CF6' : '' }}></span>
                      </label>
                      <div className="ec-toggle-label">
                        <strong style={{ color: batchUseOriginalAudio ? '#8B5CF6' : '' }}>
                          {t('useOriginalAudioTitle') || 'Originalton übernehmen'}
                        </strong>
                        <span>
                          {t('useOriginalAudioDesc') || 'Original-Audio bleibt erhalten — nur Untertitel und Übersetzungen werden generiert.'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ALLE TYPEN: Zur Allgemein-Gruppe (nur Admins sehen den Toggle; Normal-User immer 1) */}
              {isAdminUser && (
                <div className="ec-toggle-container" style={{ marginTop: 10 }}>
                  <label className="ec-switch">
                    <input type="checkbox" checked={batchAssignToGeneral}
                      onChange={(e) => setBatchAssignToGeneral(e.target.checked)} />
                    <span className="ec-slider" style={{ backgroundColor: batchAssignToGeneral ? '#10b981' : '#ef4444' }}></span>
                  </label>
                  <div className="ec-toggle-label">
                    <strong style={{ color: batchAssignToGeneral ? '#10b981' : '#ef4444' }}>
                      {t('assignToGeneral') || 'Zur Allgemein-Gruppe hinzufügen'}
                    </strong>
                    <span>
                      {batchAssignToGeneral
                        ? (t('assignToGeneralOn') || 'Inhalte werden nach Erstellung für alle Nutzer der Standard-Gruppe sichtbar.')
                        : (t('assignToGeneralOff') || '⚠ Achtung: Inhalte sind nur für Admins und den Ersteller zugänglich.')}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      ) : uploadMode === 'media' ? (
        /* --- MEDIEN UPLOAD (Photo/Video) --- */
        <>
          <div 
            className="ec-upload-area"
            onClick={() => fileInputRef.current.click()}
          >
            {formData.mediaPreviewUrl ? (
              <div className="ec-preview-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#000', overflow: 'hidden' }}>
                {formData.mediaType === 'video' ? (
                  <video 
                    src={formData.mediaPreviewUrl} 
                    className="ec-media-preview" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    controls={false} 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                  />
                ) : (
                  <img 
                    src={formData.mediaPreviewUrl} 
                    alt="Preview" 
                    className="ec-media-preview" 
                    style={{ 
                        width: 'auto', 
                        height: 'auto', 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain',
                        display: 'block' 
                    }}
                  />
                )}
                <div className="ec-change-media-overlay">
                  <span>{t('tapToChange') || 'Tap to change'}</span>
                </div>
              </div>
            ) : (
              <div className="ec-upload-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>{t('selectMedia') || 'Select Media'}</span>
              </div>
            )}
          </div>

          {/* NEU: Entfernen-Button wenn Datei ausgewählt */}
          {formData.mediaPreviewUrl && (
            <button
              className="ec-btn-remove"
              onClick={(e) => { e.stopPropagation(); handleRemoveMedia(); }}
              type="button"
            >
              ✕ {t('removeFile') || 'Datei entfernen'}
            </button>
          )}

          <input 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="video/*,image/*"
            style={{ display: 'none' }}
          />
        </>
      ) : (
        /* --- NEU: DOKUMENT UPLOAD --- */
        <>
          {formData.documentFile ? (
            /* Dokument wurde hochgeladen – Info-Box anzeigen */
            <div className="ec-document-preview">
              <div className="ec-document-icon">
                {getDocumentIcon(formData.documentMimeType)}
              </div>
              <div className="ec-document-info">
                <span className="ec-document-badge">
                  {getDocumentTypeName(formData.documentMimeType)}
                </span>
                <span className="ec-document-name" title={formData.documentFileName}>
                  {formData.documentFileName}
                </span>
                <span className="ec-document-size">
                  {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <div className="ec-document-actions">
                <button
                  className="ec-btn-doc-replace"
                  onClick={() => documentFileInputRef.current.click()}
                  type="button"
                >
                  🔄 {t('replaceFile') || 'Ersetzen'}
                </button>
                <button
                  className="ec-btn-doc-remove"
                  onClick={handleRemoveDocument}
                  type="button"
                >
                  ✕ {t('removeFile') || 'Entfernen'}
                </button>
              </div>
            </div>
          ) : (
            /* Kein Dokument – Upload-Bereich zeigen */
            <div 
              className="ec-upload-area ec-document-upload-area"
              onClick={() => documentFileInputRef.current.click()}
            >
              <div className="ec-upload-placeholder">
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>📄</div>
                <span style={{ fontWeight: 600 }}>{t('selectDocument') || 'Dokument auswählen'}</span>
                <span style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '4px' }}>
                  PDF, TXT, DOC oder DOCX · max. {window.HEJVI_SETTINGS?.maxDocumentUploadMB ?? 10} MB
                </span>
              </div>
            </div>
          )}

          {/* Info-Box: Was passiert mit dem Dokument? */}
          <div className="ec-info-box" style={{ marginTop: '16px' }}>
            <span className="ec-info-icon">🤖</span>
            <p>
              {t('documentAiInfo') || 
                'Die KI extrahiert automatisch den Inhalt des Dokuments, erstellt eine kurze Zusammenfassung (1–2 Sätze) als Videotext und nutzt die erste Seite als Vorschaubild.'}
            </p>
          </div>

          <input 
            type="file" 
            ref={documentFileInputRef}
            onChange={handleDocumentChange}
            accept=".pdf,.txt,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );

  const renderStep3_Description = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep3Title') || "Explain it"}</h2>

      {/* NEU: Dokument-Modus Info-Box */}
      {formData.isDocumentMode ? (
        <>
          <div className="ec-info-box" style={{ marginBottom: '16px' }}>
            <span className="ec-info-icon">✅</span>
            <div>
              <p style={{ marginBottom: '4px', fontWeight: 600 }}>
                {t('documentModeActive') || 'Dokument-Modus aktiv'}
              </p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                {t('documentModeDesc') || 
                  'Die KI wird den Inhalt deines Dokuments automatisch als Videotext verwenden. Du kannst optional zusätzlichen Kontext hinzufügen oder das Feld leer lassen.'}
              </p>
            </div>
          </div>
          <div className="ec-document-uploaded-badge" style={{ marginBottom: '12px' }}>
            <span>{getDocumentIcon(formData.documentMimeType)}</span>
            <span style={{ marginLeft: '8px', fontSize: '14px', color: '#6B7280' }}>
              {formData.documentFileName}
            </span>
          </div>
          <textarea 
            className="ec-textarea"
            placeholder={t('docAdditionalContextPlaceholder') || "Optionaler zusätzlicher Kontext (z.B. Zielgruppe, Fokusbereich)..."}
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={4}
          />
        </>
      ) : formData.useOriginalAudio ? (
        /* Originalton-Modus aktiv → kein Text erforderlich */
        <div className="ec-info-box" style={{ marginBottom: '16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
          <span className="ec-info-icon">🔊</span>
          <div>
            <p style={{ marginBottom: '4px', fontWeight: 600, color: '#92400e' }}>
              {t('useOriginalAudioTitle') || 'Originalton übernehmen'}
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
              {t('useOriginalAudioModeInfo') || 'Die Quellsprache des Videos wird automatisch erkannt und das Original-Audio 1:1 verwendet. Alle anderen Sprachen erhalten automatische TTS-Übersetzungen.'}
            </p>
          </div>
        </div>
      ) : formData.useVideoTranscription ? (
        /* Video-Transkription aktiv → Beschreibung wird automatisch befüllt */
        <div className="ec-info-box" style={{ marginBottom: '16px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <span className="ec-info-icon">🎙️</span>
          <div>
            <p style={{ marginBottom: '4px', fontWeight: 600, color: '#0369a1' }}>
              {t('transcribeVideoTitle') || 'Videoton als Voiceover verwenden'}
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
              {formData.useExactText
                ? (t('transcribeExactInfo') || 'Der gesprochene Text wird 1:1 transkribiert und als Voiceover übernommen. Die Untertitel bleiben synchron zum Originalbild.')
                : (t('transcribeLLMInfo') || 'Der gesprochene Text wird transkribiert. Die KI erstellt daraus ein optimiertes Voiceover-Skript in der erkannten Videosprache.')
              }
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="ec-subtitle">{t('createStep3Sub') || "What is happening in this video? Our AI will turn this into a professional narration."}</p>
          <textarea 
            className={`ec-textarea${stepError ? ' ec-textarea-error' : ''}`}
            placeholder={t('descPlaceholder') || "Describe the process step by step..."}
            value={formData.description}
            onChange={(e) => {
              setFormData({...formData, description: e.target.value});
              if (e.target.value.trim().length >= 10) setStepError('');
            }}
            rows={6}
          />
          {stepError && (
            <div style={{
              marginTop: '8px',
              padding: '10px 14px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '10px',
              color: '#DC2626',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span>⚠️</span>
              <span>{stepError}</span>
            </div>
          )}
        </>
      )}

      {/* --- TOGGLE SWITCHES ---
          "Originalton"     → canUseOriginalAudio; nur wenn Video — ganz OBEN, blendet alles andere aus
          "Videoton"        → group 0, 2, 3; nur wenn Video hochgeladen — OBEN
          "Text wörtlich"   → group 0, 2, 3 (isAdminUser); nur Foto/Video
          "Allgemein"       → group 0, 2, 3; auch im Dokument-Modus
          "Reaktionsvideo"  → group 0 only (isReactionVideoAdmin); nur Foto/Video — UNTEN
      ─────────────────────────────────────────────────────────────── */}

      {/* ── Schalter: Originalton übernehmen — canUseOriginalAudio; nur wenn Video ── */}
      {canUseOriginalAudio && !formData.isDocumentMode && formData.mediaType === 'video' && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={formData.useOriginalAudio}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  useOriginalAudio: checked,
                  // Andere Optionen deaktivieren wenn Originalton aktiv
                  ...(checked ? {
                    useVideoTranscription: false,
                    useExactText: false,
                    isReactionVideo: false,
                  } : {}),
                }));
                if (checked) {
                  setSelectedScript(null);
                  setScriptOptions([]);
                }
              }}
            />
            <span className="ec-slider" style={{backgroundColor: formData.useOriginalAudio ? '#f59e0b' : ''}}></span>
          </label>
          <div className="ec-toggle-label">
            <strong style={{color: formData.useOriginalAudio ? '#f59e0b' : ''}}>
              {t('useOriginalAudioTitle') || 'Originalton übernehmen'}
              {formData.useOriginalAudio && transcriptPreviewLanguage
                ? ` (${transcriptPreviewLanguage.toUpperCase()})`
                : ''}
            </strong>
            <span>
              {formData.useOriginalAudio
                ? (t('useOriginalAudioActiveDesc') || 'Das Video wird in der Originalsprache ohne Nachvertonung verwendet. Alle anderen Sprachen erhalten automatische TTS-Übersetzungen.')
                : (t('useOriginalAudioDesc') || 'Nur für Quellsprache: Original-Audio 1:1 verwenden statt ElevenLabs TTS. Andere Sprachen werden normal übersetzt.')
              }
            </span>
          </div>
        </div>
      )}

      {/* Wenn Originalton aktiv: alle anderen Optionen ausblenden — AUSSER "Allgemein" */}
      {!formData.useOriginalAudio && (
        <>

      {/* Schalter: Videoton als Voiceover — group 0, 2, 3; nur wenn Video hochgeladen */}
      {canUseVideoTone && !formData.isDocumentMode && formData.mediaType === 'video' && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={formData.useVideoTranscription}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData(prev => ({
                  ...prev,
                  useVideoTranscription: checked,
                }));
                if (checked) {
                  setSelectedScript(null);
                  setScriptOptions([]);
                }
                // Gecachtes Transkript verwerfen wenn Toggle geändert
                setVideoToneTranscript('');
                setVideoToneLanguage('');
              }}
            />
            <span className="ec-slider" style={{backgroundColor: formData.useVideoTranscription ? '#0ea5e9' : ''}}></span>
          </label>
          <div className="ec-toggle-label">
            <strong style={{color: formData.useVideoTranscription ? '#0ea5e9' : ''}}>
              {t('transcribeVideoTitle') || 'Videoton als Voiceover verwenden'}
            </strong>
            <span>
              {t('transcribeVideoDesc') || 'Der gesprochene Text wird transkribiert, die erkannte Sprache als Quellsprache gesetzt und Untertitel synchron zum Bild generiert.'}
            </span>
          </div>
        </div>
      )}

      {/* Schalter: Text wörtlich übernehmen (ohne Transkriptions-Modus) */}
      {canUseExactText && !formData.isDocumentMode && !formData.useVideoTranscription && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={formData.useExactText}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData(prev => ({...prev, useExactText: checked}));
                if (checked) {
                  setSelectedScript(null);
                  setScriptOptions([]);
                }
              }}
            />
            <span className="ec-slider"></span>
          </label>
          <div className="ec-toggle-label">
            <strong>{t('useExactTextTitle') || "Text exakt vertonen"}</strong>
            <span>
              {t('useExactTextDesc') || "KI-Optimierung überspringen und Beschreibung 1:1 vorlesen."}
            </span>
          </div>
        </div>
      )}

      {/* Schalter: Text wörtlich (Transkriptions-Variante — 1:1 vs KI-Skript) */}
      {canUseExactText && !formData.isDocumentMode && formData.useVideoTranscription && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={formData.useExactText}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData(prev => ({...prev, useExactText: checked}));
              }}
            />
            <span className="ec-slider"></span>
          </label>
          <div className="ec-toggle-label">
            <strong>{t('useExactTextTitle') || "Text exakt vertonen"}</strong>
            <span style={{color: '#0369a1'}}>
              {t('transcribeExactToggleDesc') || 'Transkription 1:1 übernehmen (synchron zum Bild). Aus: KI erstellt optimiertes Skript aus dem Transkript.'}
            </span>
          </div>
        </div>
      )}

      {/* Schalter: Reaktionsvideo — nur group 0 + 3, nur wenn Video hochgeladen (kein Foto, kein Dokument) */}
      {isReactionVideoAdmin && formData.mediaType === 'video' && !formData.isDocumentMode && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={formData.isReactionVideo}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData(prev => ({...prev, isReactionVideo: checked}));
              }}
            />
            <span className="ec-slider" style={{backgroundColor: formData.isReactionVideo ? '#8B5CF6' : ''}}></span>
          </label>
          <div className="ec-toggle-label">
            <strong style={{color: formData.isReactionVideo ? '#8B5CF6' : ''}}>{t('reactionVideo') || 'Reaktionsvideo'}</strong>
            <span>
              {t('setThisToReaction') || 'Markiert dieses Element als Antwort-Video (Content Type 4)'}.
            </span>
          </div>
        </div>
      )}

        </>
      )}

      {/* Schalter: Zur Allgemein-Gruppe — group 0, 2, 3; auch im Dokument-Modus, immer sichtbar */}
      {isAdminUser && (
        <div className="ec-toggle-container" style={{animation: 'fadeIn 0.3s', marginTop: '15px'}}>
          <label className="ec-switch">
            <input
              type="checkbox"
              checked={assignToGeneral}
              onChange={(e) => setAssignToGeneral(e.target.checked)}
            />
            <span className="ec-slider" style={{backgroundColor: assignToGeneral ? '#10b981' : '#ef4444'}}></span>
          </label>
          <div className="ec-toggle-label">
            <strong style={{color: assignToGeneral ? '#10b981' : '#ef4444'}}>
              {t('assignToGeneral') || 'Zur Allgemein-Gruppe hinzufügen'}
            </strong>
            <span>
              {assignToGeneral
                ? (t('assignToGeneralOn') || 'Inhalt wird nach Erstellung für alle Nutzer der Standard-Gruppe sichtbar.')
                : (t('assignToGeneralOff') || '⚠ Achtung: Dieser Inhalt wird nicht für die Allgemeinheit sichtbar sein. Er ist nur für Admins und den Ersteller zugänglich.')}
            </span>
          </div>
        </div>
      )}
      {/* ------------------------------------------- */}
    </div>
  );

  const renderScriptSelection = () => (
    <div className="ec-step-content fade-in">
        <h2 className="ec-title">{t('selectModeration') || 'Wähle den passenden Text aus'}</h2>
        <p className="ec-subtitle">
            {t('selectModerationDesc') || 'Die KI hat folgende Textvorschläge basierend auf deiner Beschreibung erstellt:'}
        </p>

        {isLoadingScripts ? (
            <div className="ec-loading-container" style={{textAlign: 'center', padding: '40px'}}>
                <div className="ec-spinner" style={{margin: '0 auto 20px'}}></div>
                <p>{t('generatingScripts') || 'Texte werden generiert...'}</p>
            </div>
        ) : (
            <div className="ec-script-options">
                {scriptOptions.map((text, idx) => {
                    const isGroup0 = user && parseInt(user.group) === 0;
                    const isOriginal = isGroup0 && idx === scriptOptions.length - 1;
                    
                    let badgeLabel = `Option ${idx + 1}`;
                    if (isGroup0) {
                        if (isOriginal) {
                            badgeLabel = t('originalText') || 'Original';
                        } else {
                            badgeLabel = `AI Option ${idx + 1}`;
                        }
                    }

                    return (
                        <div 
                            key={idx} 
                            className="ec-script-card"
                            onClick={() => handleScriptSelect(text)}
                            style={{
                                border: isOriginal ? '1px solid #10B981' : undefined
                            }}
                        >
                            <div 
                                className="ec-script-badge"
                                style={{
                                    backgroundColor: isOriginal ? '#10B981' : undefined 
                                }}
                            >
                                {badgeLabel}
                            </div>
                            <p>{stripScriptPrefix(text)}</p>
                            <button 
                                className="ec-btn-select"
                                style={{
                                    backgroundColor: isOriginal ? '#10B981' : undefined
                                }}
                            >
                                {t('select') || 'Auswählen'}
                            </button>
                        </div>
                    );
                })}
            </div>
        )}

        {!isLoadingScripts && (
            <div className="ec-actions-center" style={{marginTop: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px'}}>
                <button 
                    className="ec-btn-text" 
                    onClick={handleScriptRetry}
                    style={{color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline'}}
                >
                    ↻ {t('nothingFits') || 'Nichts passt'} ({3 - scriptRetryCount} Versuche übrig)
                </button>
                
                <button 
                    className="ec-btn ec-btn-secondary" 
                    onClick={() => setShowScriptSelection(false)}
                >
                    {t('backToDesc') || 'Zurück zur Beschreibung'}
                </button>
            </div>
        )}
    </div>
  );

  // --- Step 3.5: Superadmin Transkript-Review ---
  // Sichtbar nur für group=0 wenn useVideoTranscription + useExactText aktiv.
  const renderTranscriptReview = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">
        {t('transcriptReviewTitle') || 'Transkript prüfen'}
      </h2>
      <p className="ec-subtitle" style={{ marginBottom: '16px' }}>
        {t('transcriptReviewSub') || 'Das KI-Transkript deines Videos. Du kannst es vor der Verarbeitung korrigieren.'}
      </p>

      {isTranscriptLoading ? (
        <div className="ec-info-box" style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a' }}>
          <span className="ec-info-icon">⏳</span>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: '#92400e' }}>
              {t('transcriptReviewLoading') || '⏳ Transkribiere Audio…'}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#374151' }}>
              Das kann je nach Videolänge einige Sekunden dauern.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6B7280' }}>
            🔊 Erkannt · bearbeitbar · wird 1:1 als Voiceover gesprochen
          </div>
          <textarea
            className="ec-textarea"
            value={transcriptPreviewEdited}
            onChange={(e) => setTranscriptPreviewEdited(e.target.value)}
            rows={12}
            placeholder={t('transcriptReviewPlaceholder') || 'Transkript wird geladen…'}
            style={{ fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.6' }}
          />
          {transcriptPreviewEdited !== transcriptPreview && (
            <div style={{
              marginTop: '8px', padding: '6px 12px',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '8px', fontSize: '12px', color: '#15803d',
            }}>
              ✓ Transkript wurde bearbeitet — diese Version wird übernommen.
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderStep4_Review = () => (
    <div className="ec-step-content">
      <h2 className="ec-title">{t('createStep4Title') || "Review"}</h2>
      
      <div className="ec-review-card">
        <div className="ec-review-row">
          <span className="ec-label">{t('name')}:</span>
          <span className="ec-value">{formData.name}</span>
        </div>

        {/* NEU: Dokument ODER Medien-Anzeige im Review */}
        {formData.isDocumentMode ? (
          <div className="ec-review-row">
            <span className="ec-label">{t('document') || 'Dokument'}:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>{getDocumentIcon(formData.documentMimeType)}</span>
              <div>
                <span className="ec-value">{formData.documentFileName}</span>
                <span style={{ display: 'block', fontSize: '12px', color: '#9CA3AF' }}>
                  {getDocumentTypeName(formData.documentMimeType)} · {(formData.documentFile?.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="ec-review-row">
            <span className="ec-label">{t('media')}:</span>
            <span className="ec-value">
              {formData.mediaFile ? `${formData.mediaType.toUpperCase()} - ${(formData.mediaFile.size / 1024 / 1024).toFixed(1)} MB` : 'None'}
            </span>
          </div>
        )}

        {/* Beschreibung – im Dokument-Modus als optional markieren */}
        <div className="ec-review-row">
          <span className="ec-label">{t('description')}:</span>
          {formData.isDocumentMode && !formData.description ? (
            <span style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>
              {t('autoGeneratedFromDoc') || 'Wird automatisch aus dem Dokument generiert'}
            </span>
          ) : (
            <p className="ec-value-block">{formData.description}</p>
          )}
        </div>

        {/* Modus-Anzeige */}
        {formData.isDocumentMode ? (
            <div className="ec-review-item" style={{marginTop: '10px'}}>
                <div className="ec-review-value" style={{
                    padding: '10px 15px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    color: '#15803d',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>🤖</span>
                    <span>{t('documentAutoProcess') || 'KI extrahiert Inhalt und erstellt Zusammenfassung als Videotext.'}</span>
                </div>
            </div>
        ) : formData.useExactText ? (
            <div className="ec-review-item" style={{marginTop: '10px'}}>
                <div className="ec-review-value" style={{
                    padding: '10px 15px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1e40af',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>ℹ️</span>
                    <strong>{t('useExactTextTitle') || "Exakter Text Modus"}:</strong>
                    <span>{t('useExactTextDesc') || "Die Beschreibung wird 1:1 übernommen."}</span>
                </div>
            </div>
        ) : selectedScript && (
            <div className="ec-review-item">
                <label>{t('selectedModeration') || 'Gewählte Moderation'}:</label>
                <div className="ec-review-value" style={{
                    marginTop: '8px',
                    padding: '15px',
                    backgroundColor: '#1f2937',
                    borderRadius: '8px',
                    borderLeft: '4px solid #8b5cf6',
                    fontStyle: 'italic',
                    whiteSpace: 'pre-wrap',
                    color: '#e5e7eb',
                    fontSize: '0.95rem'
                }}>
                    "{selectedScript}"
                </div>
            </div>
        )}
      </div>

      <div className="ec-info-box">
        <span className="ec-info-icon">✨</span>
        <p>
          {formData.isDocumentMode
            ? (t('aiDocProcessingInfo') || 'Wir analysieren das Dokument, erzeugen eine Zusammenfassung als Voiceover und erstellen ein Video mit der ersten Seite als Vorschaubild.')
            : (t('aiProcessingInfo') || 'We will now compress the video, generate a professional voiceover from your description, and translate everything into 7 languages.')}
        </p>
      </div>
    </div>
  );

  const renderStep5_Processing = () => {
    const isDoc = formData.isDocumentMode;
    const hasTranscription = formData.useVideoTranscription;

    // Labels für die Stage-Punkte (visuell, nicht für die echte Steuerung)
    const stageLabels = isDoc
      ? [
          t('progressUploading')        || 'Hochladen',
          t('progressExtractingText')   || 'Text extrahieren',
          t('progressProcessingImages') || 'Seiten analysieren',
          t('progressGeneratingSummary')|| 'Zusammenfassung',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ]
      : hasTranscription
      ? [
          t('progressUploading')        || 'Hochladen',
          t('progressTranscribing')     || 'Transkribieren',
          t('progressGeneratingScript') || 'Skript',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ]
      : [
          t('progressUploading')        || 'Hochladen',
          t('progressProcessingMedia')  || 'Verarbeiten',
          t('progressGeneratingScript') || 'Skript',
          t('progressGeneratingAudio')  || 'Voiceover',
          t('progressRenderingVideo')   || 'Video rendern',
          t('progressFinalizing')       || 'Speichern',
        ];

    // Backend-label map und currentLabel werden nicht mehr angezeigt
    const displayProgress = Math.min(progressValue, 100);

    return (
      <div className="ec-step-content ec-processing" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '28px', padding: '32px 16px',
      }}>

        {/* Titel + Untertitel */}
        <div style={{ textAlign: 'center' }}>
          <h2 className="ec-title" style={{ margin: '0 0 6px' }}>
            {t('creatingElement') || 'Element wird erstellt …'}
          </h2>
          <p className="ec-subtitle" style={{ margin: 0 }}>
            {t('pleaseWait') || 'Das kann eine Minute dauern.'}
          </p>
        </div>

        {/* Kreis-Progress + Prozent */}
        <div style={{ position: 'relative', width: 108, height: 108 }}>
          <svg width="108" height="108" viewBox="0 0 108 108" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="54" cy="54" r="46" fill="none" stroke="#E5E7EB" strokeWidth="8" />
            <circle
              cx="54" cy="54" r="46"
              fill="none"
              stroke="#FF6407"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 46}`}
              strokeDashoffset={`${2 * Math.PI * 46 * (1 - displayProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.35rem', fontWeight: 700, color: '#111827',
            fontFamily: 'Inter, sans-serif',
          }}>
            {displayProgress}%
          </div>
        </div>

        {/* Stage-Punkte */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {stageLabels.map((_, i) => {
            const isDone    = i < progressStageIdx;
            const isCurrent = i === progressStageIdx;
            return (
              <div key={i} style={{
                width:  isCurrent ? 20 : 8,
                height: 8,
                borderRadius: 99,
                backgroundColor: isDone || isCurrent ? '#FF6407' : '#E5E7EB',
                opacity: isDone ? 0.5 : 1,
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }} />
            );
          })}
        </div>
      </div>
    );
  };

  // --- Dokument-Vorschau-Zwischenschritt (group 0/2/3 + optionaler Flag) ---
  const renderDocPreview = () => (
    <div className="ec-step-content">
      <h2 className="ec-title" style={{ marginBottom: 4 }}>
        {t('docPreviewTitle') || 'Vorschau prüfen'}
      </h2>
      <p className="ec-subtitle" style={{ marginBottom: 20 }}>
        {t('docPreviewSub') || 'Die KI hat Name und Beschreibung aus dem Dokument generiert. Du kannst beides anpassen.'}
      </p>

      {docPreviewLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '40px 0' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: '3px solid #f3f4f6', borderTopColor: '#f97316',
            animation: 'spin 0.8s linear infinite',
          }} />
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
            {t('docPreviewLoading') || 'Dokument wird analysiert …'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* doc_type Badge */}
          {docPreviewData?.doc_type && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600,
                background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c',
              }}>
                {docPreviewData.doc_type}
              </span>
              {(docPreviewData.doc_type_secondary || []).map((t2, i) => (
                <span key={i} style={{
                  padding: '2px 8px', borderRadius: 100, fontSize: 11,
                  background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b',
                }}>{t2}</span>
              ))}
            </div>
          )}

          {/* Name */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {t('docPreviewName') || 'Name'}
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                {t('docPreviewEditable') || '(bearbeitbar)'}
              </span>
            </label>
            <input
              type="text"
              className="ec-input"
              value={docPreviewName}
              onChange={e => setDocPreviewName(e.target.value)}
              placeholder={t('docPreviewNamePlaceholder') || 'z.B. Bedienungsanleitung Knauf G4'}
              style={{ fontSize: 15, fontWeight: 500 }}
            />
          </div>

          {/* Beschreibung */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {t('docPreviewDesc') || 'Beschreibung'}
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                {t('docPreviewEditable') || '(bearbeitbar)'}
              </span>
            </label>
            <textarea
              className="ec-textarea"
              rows={4}
              value={docPreviewDesc}
              onChange={e => setDocPreviewDesc(e.target.value)}
              placeholder={t('docPreviewDescPlaceholder') || 'KI-generierte Beschreibung …'}
              style={{ resize: 'vertical', minHeight: 90 }}
            />
          </div>

          {/* Tags (nur Anzeige) */}
          {(docPreviewData?.doc_tags || []).length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
                {t('docPreviewTags') || 'Erkannte Themen'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {docPreviewData.doc_tags.map((tag, i) => (
                  <span key={i} style={{
                    padding: '2px 9px', borderRadius: 100, fontSize: 11,
                    background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569',
                  }}>{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Fehler-Fallback */}
          {docPreviewData && !docPreviewData.doc_name && !docPreviewLoading && (
            <div style={{
              padding: '10px 14px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 8,
              fontSize: 13, color: '#92400e',
            }}>
              ⚠️ {t('docPreviewError') || 'Analyse konnte nicht abgeschlossen werden. Du kannst trotzdem fortfahren.'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderBatchSuccess = () => {
    const jobs = batchResult?.jobs || [];
    const type = batchResult?.type || 'document';
    const errors = batchResult?.errors || [];
    return (
      <div className="ec-step-content ec-success-view">
        <div className="ec-success-icon" style={{ background: '#ede9fe', color: '#6d28d9' }}>
          <span style={{ fontSize: 32 }}>📦</span>
        </div>
        <h2 className="ec-title" style={{ textAlign: 'center' }}>
          {jobs.length} Element{jobs.length !== 1 ? 'e' : ''} in Warteschlange
        </h2>
        <p className="ec-subtitle" style={{ textAlign: 'center' }}>
          {type === 'photo' ? 'Deine Fotos werden zu einem Video verarbeitet.'
            : `Jedes ${type === 'document' ? 'Dokument' : 'Video'} wird einzeln verarbeitet.`}
          {' '}Du erhältst Benachrichtigungen wenn die Elemente fertig sind.
        </p>
        {errors.length > 0 && (
          <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#dc2626' }}>
            ⚠️ {errors.length} Datei{errors.length!==1?'en':''} konnten nicht verarbeitet werden.
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns: jobs.length===1?'1fr':'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
          {jobs.map((job, idx) => (
            <div key={idx} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:'12px 8px', textAlign:'center' }}>
              <FastlaneQRCode url={job.qr_direct_url||''} size={jobs.length===1?180:120} />
              <div style={{ fontSize:11, color:'#374151', marginTop:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:130, margin:'6px auto 0' }}
                title={job.display_name||job.original_name}>
                {(job.display_name||job.original_name||`Element ${idx+1}`).substring(0,22)}
              </div>
              <div style={{ fontSize:10, color:'#94a3b8', marginTop:2 }}>⚡ In Bearbeitung</div>
            </div>
          ))}
        </div>
        <div className="ec-success-actions">
          <button className="ec-btn" onClick={() => downloadBatchQRPDF(jobs)}
            style={{ background:'#1d4ed8', color:'#fff', width:'100%' }} type="button">
            📥 QR-Codes als PDF herunterladen
          </button>
          <button className="ec-btn" onClick={() => printAllBatchLabels(jobs)} disabled={batchPrinting}
            style={{ background: batchPrinting?'#a78bfa':'#6d28d9', color:'#fff', width:'100%', cursor:batchPrinting?'not-allowed':'pointer' }} type="button">
            {batchPrinting ? `🖨️ Druckt Label ${batchPrintIdx+1}/${jobs.length}…` : jobs.length === 1 ? '🖨️ Label drucken' : `🖨️ Alle ${jobs.length} Labels drucken`}
          </button>
          <button className="ec-btn ec-btn-primary" onClick={() => navigate('/dashboard')} type="button">Zum Dashboard</button>
          <button className="ec-btn ec-btn-outline" onClick={() => { setBatchMode(false); setBatchFiles([]); setBatchResult(null); window.location.reload(); }} type="button">
            ⚡ Weitermachen
          </button>
        </div>
        <canvas ref={printCanvasRef} style={{ display:'none' }} />
      </div>
    );
  };

  const renderStep6_Success = () => {
    const isPending = createdElementData?.pending_approval === true;

    const printerButtonConfig = {
      idle:       { label: t('printQRLabel') || '🖨️ QR-Label drucken',        style: { backgroundColor: '#6d28d9', color: '#fff' }, disabled: false },
      connecting: { label: t('printerConnecting') || '🔵 Verbinde Drucker...', style: { backgroundColor: '#a78bfa', color: '#fff' }, disabled: true  },
      printing:   { label: t('printerPrinting') || '⏳ Druckt...',             style: { backgroundColor: '#a78bfa', color: '#fff' }, disabled: true  },
      done:       { label: t('printerDone') || '✅ Gedruckt!',                 style: { backgroundColor: '#10b981', color: '#fff' }, disabled: false },
      error:      { label: t('printerError') || '❌ Fehler – Erneut versuchen',style: { backgroundColor: '#ef4444', color: '#fff' }, disabled: false },
    };
    const btnCfg = printerButtonConfig[printStatus] || printerButtonConfig.idle;

    console.log('[Niimbot] Step6 render – createdElementData:', JSON.stringify(createdElementData));
    console.log('[Niimbot] printStatus:', printStatus, '| printerConnected:', printerConnected);
    console.log('[ElementCreator] pending_approval:', isPending);

    // ── PENDING-VIEW: Supervisor-Modus aktiv — Video wartet auf Freigabe ──
    if (isPending) {
      return (
        <div className="ec-step-content ec-success-view" style={{ textAlign: 'center' }}>

          {/* Pending Icon */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#fef3c7', border: '2px solid #f59e0b',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 36, margin: '0 auto 20px',
          }}>
            ⏳
          </div>

          <h2 className="ec-title" style={{ textAlign: 'center', marginBottom: 12 }}>
            {t('elementPendingTitle') || 'Dein Video wird geprüft'}
          </h2>

          <p className="ec-subtitle" style={{
            textAlign: 'center', maxWidth: 320, margin: '0 auto 24px',
            lineHeight: 1.6, color: '#6b7280',
          }}>
            {t('elementPendingDesc') || 'Dein Video wurde hochgeladen und wird von einem Administrator geprüft.'}
          </p>

          {/* Info-Box */}
          <div style={{
            background: '#fffbeb', border: '1px solid #f59e0b',
            borderRadius: 10, padding: '14px 18px',
            maxWidth: 320, margin: '0 auto 28px',
            display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
            <span style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              {t('elementPendingHint') || 'Du erhältst eine Benachrichtigung, sobald dein Video freigegeben wurde.'}
            </span>
          </div>

          {/* Aktions-Buttons */}
          <div className="ec-success-actions">
            <button
              className="ec-btn ec-btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              {t('backToDashboard') || 'Back to Dashboard'}
            </button>

          </div>
        </div>
      );
    }

    // ── NORMAL SUCCESS-VIEW ──────────────────────────────────────────────────
    return (
      <div className="ec-step-content ec-success-view">
        <div className="ec-success-icon">
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h2 className="ec-title" style={{textAlign: 'center'}}>
          {t('elementCreatedSuccess') || 'Element erstellt!'}
        </h2>

        {/* KI-generierter Name */}
        {createdElementData?.generated_name && (
          <div style={{
            textAlign: 'center', marginBottom: 4,
            fontSize: 15, fontWeight: 600, color: '#111827',
          }}>
            {createdElementData.generated_name}
          </div>
        )}

        <p className="ec-subtitle" style={{textAlign: 'center'}}>
          {t('elementReadyDesc') || 'Dein Element wurde erfolgreich erstellt und verarbeitet.'}
        </p>

        {/* ── Warnungen aus dem Backend (z.B. kein Audio-Stream) ── */}
        {createdElementData?.warnings?.includes('no_audio_stream') && (
          <div style={{
            background: '#fffbeb', border: '1px solid #f59e0b',
            borderRadius: 10, padding: '12px 16px',
            margin: '0 0 16px',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>🔇</span>
            <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              <strong style={{ display: 'block', marginBottom: 2 }}>
                {t('warnNoAudioStreamTitle') || 'Originalton konnte nicht verwendet werden'}
              </strong>
              {t('warnNoAudioStreamDesc') || 'Das hochgeladene Video enthält keinen Ton und wird ohne Audiospur veröffentlicht.'}
            </div>
          </div>
        )}

        {/* Fast-lane: In-Bearbeitung-Hinweis */}
        {createdElementData?.is_queued && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12,
            padding: '14px 18px', marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>⚡</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 4 }}>
                Im Hintergrund in Bearbeitung
              </div>
              <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
                Dein Element wird gerade verarbeitet (TTS, Video, Vektorisierung).
                Du erhältst eine Benachrichtigung wenn es fertig ist.
                Der QR-Code ist sofort scan-bereit.
              </div>
            </div>
          </div>
        )}

        {/* --- QR CODE ANZEIGE --- */}
        {createdElementData && createdElementData.qr_direct_url && !createdElementData.is_queued && (
          <div className="ec-qr-container">
              <img 
                  src={getSecureMediaUrl(createdElementData.qr_direct_url)} 
                  alt="QR Code" 
                  className="ec-qr-image"
              />
              <span className="ec-qr-hint">
                  {t('scanToStart') || 'Scan to view on mobile'}
              </span>
          </div>
        )}

        {/* Fast-lane: QR-Code clientseitig generieren (kein Server-Bild vorhanden) */}
        {createdElementData?.is_queued && createdElementData.qr_direct_url && (
          <div className="ec-qr-container">
            <FastlaneQRCode url={createdElementData.qr_direct_url} size={160} />
            <span className="ec-qr-hint">
              {t('scanToStart') || 'Scan to view on mobile'}
            </span>
          </div>
        )}

        {createdElementData && !createdElementData.qr_direct_url && (
          <div style={{marginBottom:'8px', padding:'8px', backgroundColor:'#fef3c7', borderRadius:'8px', fontSize:'0.75rem', color:'#92400e'}}>
            ⚠️ Kein qr_direct_url. API-Felder: {Object.keys(createdElementData).join(', ')}
          </div>
        )}

        <canvas ref={printCanvasRef} style={{ display: 'none' }} />

        <div className="ec-success-actions">

          <button
            className="ec-btn"
            onClick={printQRLabel}
            disabled={btnCfg.disabled}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              cursor: btnCfg.disabled ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.95rem',
              ...btnCfg.style
            }}
          >
            {btnCfg.label}
          </button>

          {printStatus === 'idle' && !printerConnected && (
            <p style={{ margin: '-8px 0 4px', fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center' }}>
              {t('bluetoothHint') || 'Verbindet per Bluetooth – Niimbot B1 in der Liste auswählen'}
            </p>
          )}
          {printStatus === 'error' && (
            <p style={{ margin: '-8px 0 4px', fontSize: '0.72rem', color: '#ef4444', textAlign: 'center' }}>
              {t('printerErrorHint') || 'Drucker eingeschaltet? Nur Chrome/Edge unterstützt Web Bluetooth.'}
            </p>
          )}
          {/* Video-Readiness Indikator + "Element ansehen"-Button */}
          {createdElementData?.videoUrl && videoReady !== true && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 10, padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              marginBottom: 4,
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                border: '2px solid #d1d5db', borderTopColor: '#FF6B00',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                {t('videoProcessingHint') || 'Video wird gerade fertiggestellt. Warte noch, um es direkt anzusehen, oder arbeite einfach weiter.'}
              </span>
            </div>
          )}
          {createdElementData?.hash_id ? (
            <button
              className="ec-btn ec-btn-primary"
              disabled={createdElementData?.videoUrl ? videoReady !== true : false}
              style={{ opacity: (createdElementData?.videoUrl && videoReady !== true) ? 0.45 : 1,
                       cursor: (createdElementData?.videoUrl && videoReady !== true) ? 'not-allowed' : 'pointer' }}
              onClick={() => {
                if (createdElementData?.videoUrl && videoReady !== true) return;
                navigate(`/element-feed/${createdElementData.hash_id}`);
              }}
            >
              {(createdElementData?.videoUrl && videoReady !== true)
                ? (t('viewElement') || 'Element jetzt ansehen')
                : (t('viewElement') || 'Element jetzt ansehen')}
            </button>
          ) : (
            <button
              className="ec-btn ec-btn-primary"
              onClick={() => navigate('/my-courses')}
            >
              {t('goToMyCourses') || "Go to My Courses"}
            </button>
          )}
          
          <button 
            className="ec-btn ec-btn-secondary" 
            onClick={() => navigate('/dashboard')}
          >
            {t('backToDashboard') || "Back to Dashboard"}
          </button>
          


          {/* Fast-lane: Sofort weitermachen ohne auf Fertigstellung warten */}
          {createdElementData?.is_queued && (
            <button
              className="ec-btn ec-btn-primary"
              onClick={() => window.location.reload()}
              style={{ background: '#d97706', borderColor: '#d97706', marginTop: 4 }}
            >
              ⚡ Nächstes Element einreichen
            </button>
          )}
        </div>
      </div>
    );
  };

  // --- MAIN RENDER ---

  if (isProcessing && !batchSubmitting) {
    return (
      <GlobalLayout title={t('elementCreator') || "Element Creator"} showBackButton={false}>
        <div className="ec-container">
          {renderStep5_Processing()}
        </div>
      </GlobalLayout>
    );
  }

  return (
    <GlobalLayout 
      title={t('createElement') || "Create Element"} 
      showBackButton={true} 
      onBackClick={step === 1 ? () => navigate(-1) : handleBack}
    >
      <div className="ec-container">
        {/* Progress Indicator */}
        <div className="ec-progress-bar">
          <div className={`ec-progress-step ${step >= 1 ? 'active' : ''}`}></div>
          {!formData.isDocumentMode && (
            <div className={`ec-progress-step ${step >= 2 ? 'active' : ''}`}></div>
          )}
        </div>

        {/* Error Screen — ersetzt Step 4 komplett wenn Fehler aufgetreten */}
        {error && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: '20px', padding: '32px 16px',
            textAlign: 'center', minHeight: '60vh',
          }}>
            {/* Icon */}
            <div style={{ fontSize: 56 }}>
              {uploadErrorType === 'timeout' ? '⏳' : '⚠️'}
            </div>

            {/* Titel */}
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>
              {uploadErrorType === 'timeout'
                ? 'Verarbeitung dauert länger als erwartet'
                : 'Erstellung fehlgeschlagen'}
            </h2>

            {/* Beschreibung */}
            <p style={{ margin: 0, color: '#6B7280', fontSize: '0.95rem', maxWidth: 320, lineHeight: 1.5 }}>
              {uploadErrorType === 'timeout'
                ? 'Das Dokument wird noch verarbeitet. Bitte warte einen Moment und prüfe deine Inhalte — das Element wurde möglicherweise trotzdem erstellt.'
                : error}
            </p>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 300 }}>
              {/* Retry */}
              <button
                className="ec-btn ec-btn-success"
                onClick={() => {
                  setError('');
                  setUploadErrorType(null);
                  setProgressValue(0);
                  setProgressStageIdx(0);
                  if (batchMode) { handleBatchSubmit(); } else { handleSubmit(); }
                }}
              >
                ↻ Erneut versuchen
              </button>

              {!batchMode && uploadErrorType === 'timeout' && (
                <button
                  className="ec-btn ec-btn-secondary"
                  onClick={() => navigate('/my-courses')}
                >
                  Meine Inhalte prüfen
                </button>
              )}

              <button
                className="ec-btn ec-btn-secondary"
                onClick={() => {
                  setError('');
                  setUploadErrorType(null);
                  if (batchMode) { setStep(1); } else { setStep(formData.isDocumentMode ? 1 : 2); }
                }}
              >
                ← Zurück zum Formular
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="ec-content-wrapper" style={{ display: error ? 'none' : undefined }}>
          {showScriptSelection ? (
              renderScriptSelection()
          ) : showTranscriptReview ? (
              renderTranscriptReview()
          ) : (
              <>
                {showDocPreview && renderDocPreview()}
                {!showDocPreview && step === 1 && renderStep2_Media()}
                {!showDocPreview && step === 2 && renderStep3_Description()}
                {step === 6 && (batchResult ? renderBatchSuccess() : renderStep6_Success())}
              </>
          )}
        </div>

        {/* Footer Navigation */}
        {/* Dokument-Vorschau Footer */}
        {showDocPreview && !error && (
          <div className="ec-footer">
            <button
              className="ec-btn ec-btn-secondary"
              onClick={() => setShowDocPreview(false)}
              disabled={docPreviewLoading}
            >
              {t('back') || 'Zurück'}
            </button>
            <button
              className="ec-btn ec-btn-success"
              onClick={handleDocPreviewConfirm}
              disabled={docPreviewLoading}
            >
              {t('docPreviewConfirm') || 'Element erstellen'}
            </button>
          </div>
        )}

        {/* Batch-Footer im fixen Bereich — kein Overlap mit FAB */}
        {batchMode && step === 1 && !error && batchStep === 1 && (
          <div className="ec-footer">
            <button className="ec-btn ec-btn-success" style={{ flex: 1 }}
              disabled={batchFiles.length === 0}
              onClick={() => setBatchStep(2)} type="button">
              {t('next') || 'Weiter'} →
            </button>
          </div>
        )}
        {batchMode && step === 1 && !error && batchStep === 2 && (
          <div className="ec-footer">
            <button className="ec-btn ec-btn-secondary"
              onClick={() => setBatchStep(1)} type="button">
              {t('back') || 'Zurück'}
            </button>
            <button className="ec-btn ec-btn-success" style={{ flex: 1 }}
              disabled={batchSubmitting}
              onClick={handleBatchSubmit} type="button">
              {batchSubmitting
                ? '⧗ Wird hochgeladen…'
                : `📦 ${batchFiles.length} ${batchType==='photo'?'Foto':batchType==='document'?'Dokument':'Video'}${batchFiles.length!==1?'s':''} einreichen`}
            </button>
          </div>
        )}

        {step <= 2 && !batchMode && !showDocPreview && !showScriptSelection && !showTranscriptReview && !error && (
          <div className="ec-footer">
            {step > 1 && (
              <button className="ec-btn ec-btn-secondary" onClick={handleBack}>
                {t('back') || 'Zurück'}
              </button>
            )}
            {/* Step 1 Media: Weiter → Step 2 | Step 1 Dok + Step 2: direkt erstellen */}
            {(step === 1 && !formData.isDocumentMode) ? (
              <button
                className="ec-btn ec-btn-primary"
                onClick={handleNext}
              >
                {t('next') || 'Weiter'}
              </button>
            ) : (
              <button
                className="ec-btn ec-btn-success"
                onClick={handleNext}
                disabled={isTranscriptLoading}
                style={isTranscriptLoading ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
              >
                {isTranscriptLoading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px', height: '14px',
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite',
                      marginRight: '8px',
                      verticalAlign: 'middle',
                    }} />
                    {t('transcribing') || 'Transkribiere…'}
                  </>
                ) : (
                  t('create') || 'Element erstellen'
                )}
              </button>
            )}
          </div>
        )}

        {/* Footer für Transkript-Review (Step 3.5) */}
        {showTranscriptReview && !error && (
          <div className="ec-footer">
            <button
              className="ec-btn ec-btn-secondary"
              onClick={() => {
                setShowTranscriptReview(false);
                setTranscriptPreview('');
                setTranscriptPreviewEdited('');
                setTranscriptPreviewLanguage('');
                setTranscriptPreviewSegments([]);
              }}
            >
              {t('back') || 'Zurück'}
            </button>
            <button
              className="ec-btn ec-btn-success"
              disabled={isTranscriptLoading}
              onClick={() => {
                setShowTranscriptReview(false);
                setError('');
                setStepError('');
                handleSubmit();
              }}
            >
              {isTranscriptLoading ? '⏳' : (t('create') || 'Element erstellen')}
            </button>
          </div>
        )}
        {/* ── Foto-Lightbox ─────────────────────────────────────────────────────
             Klick auf Thumbnail in der Per-Slide-Übersicht öffnet Vollbild-Modal.
             Schließen: ✕-Button ODER Klick auf den dunklen Overlay-Bereich.        */}
        {photoLightboxIdx !== null && photoThumbs[photoLightboxIdx] && (
          <div
            onClick={() => setPhotoLightboxIdx(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.87)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}
          >
            {/* Innerer Container — stopPropagation verhindert Schließen beim Bild-Klick */}
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative', maxWidth: '90vw', maxHeight: '90vh',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              }}
            >
              {/* Schließen-Button */}
              <button
                onClick={() => setPhotoLightboxIdx(null)}
                aria-label="Schließen"
                style={{
                  position: 'absolute', top: -14, right: -14, zIndex: 1,
                  width: 32, height: 32, borderRadius: '50%',
                  background: '#fff', border: '1px solid #e2e8f0',
                  cursor: 'pointer', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.30)',
                  lineHeight: 1, padding: 0,
                }}
              >✕</button>

              {/* Vollbild */}
              <img
                src={photoThumbs[photoLightboxIdx]}
                alt={batchFiles[photoLightboxIdx]?.name || ''}
                style={{
                  maxWidth: '100%', maxHeight: '80vh',
                  borderRadius: 10, objectFit: 'contain',
                  boxShadow: '0 4px 32px rgba(0,0,0,0.55)',
                  display: 'block',
                }}
              />

              {/* Dateiname + Position */}
              <div style={{ color: '#fff', fontSize: 12, textAlign: 'center', opacity: 0.82, lineHeight: 1.4 }}>
                <span style={{
                  marginRight: 8, background: 'rgba(255,255,255,0.18)',
                  borderRadius: 4, padding: '2px 7px', fontWeight: 700,
                }}>
                  {photoLightboxIdx + 1} / {batchFiles.length}
                </span>
                {batchFiles[photoLightboxIdx]?.name}
              </div>
            </div>
          </div>
        )}

      </div>
    </GlobalLayout>
  );
};

export default ElementCreator;
